import { createLabelColumnFormatter, messageForConnectionType, UseRenderLabelsParams} from '@tableau/catalog-ui-temp';
import * as CertificationWatermark from '@tableau/certification-watermark';
import { BooleanBaseIcon } from '@tableau/icons/BooleanBaseIcon';
import { CalendarBaseIcon } from '@tableau/icons/CalendarBaseIcon';
import { CalendarClockIcon } from '@tableau/icons/CalendarClockIcon';
import { GlobeBaseIcon } from '@tableau/icons/GlobeBaseIcon';
import { NotificationInfoOutlineIcon } from '@tableau/icons/NotificationInfoOutlineIcon';
import { NumberBaseIcon } from '@tableau/icons/NumberBaseIcon';
import { TextStringIcon } from '@tableau/icons/TextStringIcon';
import { SiteUrls } from '@tableau/shared-urls';
import { TabStyles, Tooltip } from '@tableau/widgets';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';
import _, { Dictionary } from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { L10n } from '~commonInfra/L10n/L10n';
import { HelpService } from '~commonInfra/services/HelpService';
import { IReactTranslateService, TypeUnsafeTranslation } from '~commonInfra/services/IReactTranslateService';
import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { ServerUrls } from '~commonInfra/url/ServerUrls';
import { sanitize } from '~commonInfra/utils/Sanitize';
import { ExtractEncryptionMode } from '~commonInfra/utils/sites/SitePropertyTypes';
import { localizeIdentityMigrationFailureType } from '~src/authentication/models/IdentityMigrationFailureType';
import { BackgroundJobExtendedInfoDialogCell } from '~src/backgroundJob/components/BackgroundJobExtendedInfoDialogCell';
import { IBackgroundJobInfo } from '~src/backgroundJob/types/BackgroundJobs';
import {
  getBackgroundJobStatusTooltip,
  getIconForBackgroundJob,
  IBackgroundJob,
  localizeBackgroundJobState,
  localizeBackgroundJobTaskType,
} from '~src/backgroundJob/types/IBackgroundJob';
import { CategoryPopover } from '~src/catalog/components/listView/CategoryPopover';
import { urlForContentType } from '~src/catalog/components/listView/DataQualityIndicatorCell';
import { LabelValueCell } from '~src/catalog/components/listView/LabelValueCell';
import { LabelVisibilityCell } from '~src/catalog/components/listView/LabelVisiblityCell';
import {
  GraphDatabase,
  GraphTable,
  mapGraphTypeToContentInfoForActions
} from '~src/catalog/models/mappers/DatabaseAndTableMapper';
import { Databases } from '~src/catalog/resources/Databases';
import { Tables } from '~src/catalog/resources/Tables';
import { Formatters, Messages } from '~src/codegen/Localize';
import { CollectionVisibility } from '~src/Collections/space/CollectionModels';
import { showDMAwareness } from '~src/commonInfra/components/DMAwareness/DmAwarenessUtils';
import { isPhoneSizedScreenOrUserProbablyOnMobile } from '~src/commonInfra/utils/MobileHelper';
import { ConnectedApplicationType } from '~src/connectedApplications/models/ConnectedApplicationInfo';
import { findEstimatedDiskUsage, findLastMonthJobAvgTime, getAccelerationStatusForViewSpace, getAccelerationStatusTooltipContentForViewSpace, getAdminAccelerationStatusForTaskPage, getAdminAccelerationStatusTooltipContentForTaskPage, showAccelerationStatusInViewSubspace } from '~src/dataAcceleration/utils/DataAccelerationUtils';
import {
  getMonitorDataChangesStatusForViewSpace,
  getMonitorDataChangesStatusTooltipContentForViewSpace,
  showMonitorDataChangesInViewSubspace,
} from '~src/dataChangeRadar/utils/AuthorConfiguredDCRUtils';
import { IDataConnectionInfo } from '~src/datasource/dataConnection/models/IDataConnectionInfo';
import { IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { getCommonDatasourceInfo } from '~src/datasource/models/mappers/CommonDatasourceInfo';
import { DataSources } from '~src/datasource/resources/Datasources';
import { DataAlertDataGridCells } from '~src/dda/components/listView/DataAlertGridCells';
import { AlertCell, IAlert } from '~src/dda/components/listView/dataGridCells/AlertCell';
import { ManageDataAlertRecipientsDialog } from '~src/dda/components/ManageDataAlertRecipientsDialog';
import { IDataAlertInfo } from '~src/dda/models/IDataAlertInfo';
import { DeviceType } from '~src/devicePreview/models/DeviceType';
import { ContentDraftStatus } from '~src/drafts/serverApi/types/ContentDraft';
import { extractWorkbookDraftEditUrlInfo } from '~src/drafts/utils/DraftUtils';
import { RemoteRefreshTypes } from '~src/extractRefresh/serverApi/requests/remoteExtractRefresh/GetRemoteRefreshSchedulesRequest';
import { EncryptionTooltip } from '~src/extractTask/components/spaces/tooltips/EncryptionTooltip';
import { IExtractTaskInfo } from '~src/extractTask/models/IExtractTaskInfo';
import { ExtractTaskType, IExtractServiceTask } from '~src/extractTask/serverApi/types/IExtractTask';
import {
  FlowOrLinkedTaskOutputStepsWithIconsCell,
  FlowParameterRunWithOverrideCell,
  FlowParameterWithOverrideCell,
  FlowRefreshTypeCell,
  FlowRunDurationCell,
  FlowRunHistoryErrorsCell,
  FlowRunTypeCell,
  FlowScheduleTypeCell,
  FlowTaskOutputStepsWithIconsCell,
  FlowTaskStatusWithIconsCell,
} from '~src/flow/components/listView/FlowCells';
import { RunFlowTaskCell } from '~src/flow/components/listView/RunFlowTaskCell';
import { FlowOutputStepRunListItemInfo } from '~src/flow/models/FlowOutputStepRunInfo';
import { getFlowOutputStepRunIconClass } from '~src/flow/utils/FlowIconUtils';
import { localizeFlowRunHistoryStatus } from '~src/flow/utils/FlowStatusUtils';
import { LinkedTaskWithCountCell } from '~src/linkedTasks/components/LinkedTaskWithCountCell';
import { areLinkedTasksEnabled } from '~src/linkedTasks/utils/LinkedTaskHelpers';
import { MetricUpdatedCell, MetricValueCell } from '~src/metrics/components/listView/dataGridCells/MetricCells';
import { IConnectedViewInfo } from '~src/metrics/models/IMetricInfo';
import { ILocationInfo, LocationTypes } from '~src/personalSpace/model/ILocationInfo';
import { PublishedConnections } from '~src/publishedConnection/resources/PublishedConnections';
import { PublishedConnectionExtractRefreshLevel } from '~src/publishedConnection/types/PublishedConnectionExtractRefreshLevel';
import { getPublishedConnectionExtractRefreshLevel } from '~src/publishedConnection/utils/PublishedConnectionTaskUtil';
import { getPublishedConnectionTableIcon } from '~src/publishedConnectionTable/contentDefinitions/PublishedConnectionTableDefinition';
import { IPublishedConnectionTableBase } from '~src/publishedConnectionTable/models/IPublishedConnectionTableInfo';
import { TableauDataType } from '~src/publishedConnectionTable/types/TableauDataType';
import { ResourceGovernanceGridCells } from '~src/schedule/components/ResourceGovernanceGridCells';
import { SchedulesGridCells } from '~src/schedule/components/SchedulesGridCells';
import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';
import { serverScheduleUrl, siteScheduleUrl } from '~src/schedule/utils/ScheduleUtils';
import {
  getSessionManagementServiceInstance,
  SessionManagementService,
} from '~src/sessionManagement/SessionManagementService';
import { QuickTooltip } from '~src/settings/extensions-settings/dashboard/QuickTooltip';
import { ISiteSettingsForServerAdmin } from '~src/settings/types/ISite';
import { SharedViewsCell } from '~src/sharing/components/listView/SharedViewsCell';
import { ISharedWithMeContentInfo } from '~src/sharing/models/ISharedWithMeContentInfo';
import { SiteGridCells } from '~src/siteManagement/components/allSitesSpace/SiteGridCells';
import { SiteRolesHelper } from '~src/siteManagement/helpers/SiteRolesHelper';
import { ISiteNameWithId } from '~src/siteManagement/serverApi/types/ISiteNameWithId';
import {
  getContentSpaceDefinition,
  getExplorableItemDefinition,
} from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemDefinition } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsTypes';
import { AnyDatabaseType, getGraphQLId } from '~src/spacesPlatform/models/CommonDatabaseInfo';
import { AnyTableType, getGraphQLId as getTableGraphQlId } from '~src/spacesPlatform/models/CommonTableInfo';
import { ISpaceItemDetailInfo } from '~src/spacesPlatform/models/ISpaceItemDetailInfo';
import { MapperHelper } from '~src/spacesPlatform/models/mappers/MapperHelper';
import {
  IExplorableItemTypeInfo,
  IFavoritableItemInfo,
  IItemStatisticsInfo
} from '~src/spacesPlatform/models/resourceInfoParts';
import { IPublishedConnectionWithTablesItem } from '~src/spacesPlatform/models/resourceParts';
import { SortOptions } from '~src/spacesPlatform/models/sort/CommonSortOptions';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';
import { ItemsSet } from '~src/spacesPlatform/utils/ItemsSet';
import { TextileHelper } from '~src/spacesPlatform/utils/TextileHelper';
import {
  ScheduleDescriptionCellWithIcon,
  ScheduleFrequencyDescriptionCell,
} from '~src/subscription/components/listView/dataGridCells/ScheduleFrequencyDescriptionCells';
import { SubscriptionNextUpdateDescriptionCell } from '~src/subscription/components/listView/dataGridCells/SubscriptionNextUpdateDescriptionCell';
import { SubscriptionGrid } from '~src/subscription/components/SubscriptionGrid';
import { ISubscriptionInfo } from '~src/subscription/models/ISubscriptionInfo';
import { ProfilePicture } from '~src/userManagement/components/userProfilePicture/ProfilePicture';
import { GroupDataGridCells } from '~src/userManagement/groups/components/space/GroupDataGridCells';
import { IGroupInfo } from '~src/userManagement/groups/models/IGroupInfo';
import { IGroupSetInfo } from '~src/userManagement/groupsets/models/IGroupSetInfo';
import { UserDataGridCells } from '~src/userManagement/users/components/siteUsersSpace/SiteUserDataGridCells';
import { IServerUserInfo } from '~src/userManagement/users/models/IServerUserInfo';
import { ISiteUserInfo } from '~src/userManagement/users/models/ISiteUserInfo';
import { UserMapper } from '~src/userManagement/users/models/mappers/UserMapper';
import { SiteRoles } from '~src/userManagement/users/serverApi/types/ISiteRole';
import { IView } from '~src/views/ServerApi/types/IView';
import { IWorkbookInfo } from '~src/workbook/models/IWorkbookInfo';

import { ExplorableItemType, getKeyForExplorableItem, isSpaceInfoItemType } from '../../models/ContentType';
import { IContentInfo } from '../../models/IContentInfo';
import { OrderKeys } from '../../models/OrderKeys';
import {
  isCollectionsSpace,
  isDatasourceConnectionSubSpace,
  isRecentsSpace,
  isScheduleSpace,
  isSharedWithMeSpace,
  isSingleCollectionSpace,
  isSiteSpace,
  isSiteUserSpace,
  isWorkbookSpace,
  SpaceInfo,
} from '../../state/SpaceInfo';
import { SpaceHeaderTestIds } from '../header/SpaceHeader';
import { SpacesSiteUrls } from '../spaceComponents/SpacesSiteUrls';
import { GreyedOutTextStyle, HiddenUserStyles } from '../spaceComponents/SpacesStyles';
import { CertifiableConnectsToCell, ConnectionHoverPanel, ConnectsToCell } from './DataConnectionComponents';
import { DataConnectionDataGridCells } from './DataConnectionDataGridCells';
import { ActionCellOptions, ActionsMenuCell } from './dataGridCells/ActionsCells';
import { CheckboxCell } from './dataGridCells/CheckboxCell';
import { LinkWithCollectionVisibility } from './dataGridCells/CollectionNameCellWithVisiblity';
import { ContentTypeCell, ContentTypeTextCell } from './dataGridCells/ContentTypeCell';
import { DateTimeCell } from './dataGridCells/DateTimeCells';
import { FavoriteCell } from './dataGridCells/FavoriteCell';
import {
  InteractiveIconTooltipCell,
  InteractiveImageIconTooltipCell
} from './dataGridCells/InteractiveIconTooltipCells';
import { LastRefreshedCell } from './dataGridCells/LastRefreshedCell';
import { ILink, LinkCell } from './dataGridCells/LinkCell';
import { LinkCellWithAccessStatus } from './dataGridCells/LinkCellWithAccessStatus';
import { ILinkWithDraftStatus, LinkWithDraftStatus } from './dataGridCells/LinkCellWithDraftStatus';
import { LinkedTaskCell } from './dataGridCells/LinkedTaskCell';
import {
  FixedValueCell,
  LocalizedCountCell,
  LocalizedCountCellWithBar,
  LocalizedFloatCell,
  PlainTextCell,
  PlainTextCellWithIcon,
  PlainTextCellWithNestedValue,
  PlainTextCellWithNestedValueAndTransform,
  PlainTextCellWithTransform,
  PlainTextCellWithTransformAndTooltip,
  SizeCell,
} from './dataGridCells/PlainTextAndNumberCells';
import {
  SelectableSortKeyColumnContext,
  SelectableSortKeyColumnItem,
  SelectableSortKeyColumnOptions,
  SelectableSortKeyDataGridColumn,
} from './SelectableSortKeyDataGridColumn';
import { SortableHeader } from './SortableHeader';
import {
  AlertCellHeader,
  CenterAlignedCell,
  DataQualityWarningDisabledCellHeader,
  RightAlignedCell,
  RightAlignedHeader,
  StyledCellFormatter} from './StyledCellWrapper';
import { CommonColumn } from './types/Columns';
import { getCertificationActivityFromRowData, getWarningSeverityFromRowData } from './utils/LabelColumnFormatterHelper';

export const IconColumnPreferredWidth = 44;

export type ColumnFactory = (params: ListViewGridColumnGeneratorParams) => DataGrid.Column;

/**
 * Columns can be defined in either of two ways:
 * - As a value of the CommonColumn enum with a corresponding column definition in this file's switch statement
 * - As a factory function that takes in a subset of the params and returns a DataGrid.Column object
 */
export type ColumnType = CommonColumn | ColumnFactory;

export interface ListViewGridColumnGeneratorParams {
  readonly addContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly selectedItems: ItemsSet<IExplorableItemTypeInfo>;
  readonly selectItem: (item: IContentInfo) => void;
  readonly deselectItem: (item: IContentInfo) => void;
  readonly urls: SpacesSiteUrls;
  readonly contentFilter: ReadonlyArray<ExplorableItemType>;
  readonly showingMixedContent: boolean;
  readonly currentSort: ReadonlyArray<IOrderClause>;
  readonly changeSort: (field: string) => void;
  readonly setSortOrder: (order: ReadonlyArray<IOrderClause>) => void;
  readonly displayingItems: boolean;
  readonly isSessionSiteOrServerAdmin: boolean;
  readonly spaceInfo: SpaceInfo;
  readonly displaySchedulesInClientTimezone: boolean;
  readonly displayScheduleDescriptionAsName: boolean;
  readonly inServerArea: boolean;
  readonly flowFailureThresholdForRunPrevention: number;
}

export class ListViewGridColumnGenerator {

  private params: ListViewGridColumnGeneratorParams;

  private server: SessionManagementService;
  private siteUrls: SiteUrls;

  public constructor(params: ListViewGridColumnGeneratorParams) {
    this.params = params;
    this.server = getSessionManagementServiceInstance();
    this.siteUrls = new SiteUrls({ siteUrlName: this.server.sessionSiteUrlName() });
  }

  public updateParams(paramOverrides: Partial<ListViewGridColumnGeneratorParams>) {
    this.params = { ...this.params, ...paramOverrides };
  }

  public getColumn(columnDefinition: ColumnType): DataGrid.Column | null {
    if (columnDefinition instanceof Function) {
      return columnDefinition({ ...this.params });
    } else {
      return this.columnFromEnum(columnDefinition);
    }
  }

  private columnFromEnum(columnDefinition: CommonColumn): DataGrid.Column | null {
    const sortableHeaderFormatter = SortableHeader.Formatter(this.params.currentSort, this.params.changeSort);
    const sortableHeaderFormatterWithOverrides = (styleOverrides?: React.CSSProperties) => {
      return SortableHeader.Formatter(this.params.currentSort, this.params.changeSort, undefined, styleOverrides);
    };
    const showEncryptionTooltip: boolean = this.server.isExtractEncryptionEnabledOmniSwitch() && this.server.getExtractEncryptionModeForSite() !== ExtractEncryptionMode.DISABLED;
    const subscriptionOnExtractRefreshFn = (row: DataGrid.Row) => {
      const subscription = row.data as ISubscriptionInfo;
      return subscription.refreshExtractTriggered;
    };

    switch (columnDefinition) {
      case CommonColumn.Select:
        return {
          name: CommonColumn.Select,
          dataKey: 'selected',
          headerText: '',
          resizable: false,
          sortable: false,
          // Account for first column's left margin
          preferredWidth: this.params.displayingItems ? IconColumnPreferredWidth + TabStyles.Spacing.DefaultSpace : 0,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CheckboxCell({
            onCheckboxClicked: this.onSelectedCheckboxClicked,
            isRowSelected: this.isRowSelected,
            checkboxCellTestIdFn: this.checkboxCellTestId,
            isRowDisabled: this.isRowDisabled,
          })
        };

      case CommonColumn.Favorite:
        return {
          name: CommonColumn.Favorite,
          dataKey: 'favorite',
          headerText: '',
          resizable: false,
          sortable: false,
          preferredWidth: this.params.displayingItems ? IconColumnPreferredWidth : 0,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: FavoriteCell({
            onAddFavorite: this.onAddFavorite,
            onRemoveFavorite: this.onRemoveFavorite,
            isFavoritable: this.isFavoritable,
            favoriteCellTestIdFn: this.favoriteCellTestId,
            isAccessDenied: this.isAccessDenied,
          })
        };

      case CommonColumn.LabelWarning:
        if(showDMAwareness()){
          const sortableHeaderFormatterWithSiteURLs = SortableHeader.Formatter(this.params.currentSort, this.params.changeSort,
            <Tooltip getContent={() => <span>{Messages.dqwInformationHeaderToolTip()}</span>}>
              <NotificationInfoOutlineIcon size={16} alt={Messages.dqwInformationHeaderToolTip()} color={'rgb(0,0,0,0)'}/>
            </Tooltip>,
            undefined,this.siteUrls);
          return {
            name: columnDefinition,
            dataKey: 'dataqualityindicator',
            headerText: '',
            resizable: false,
            sortable: true,
            preferredWidth: this.params.displayingItems ? IconColumnPreferredWidth : 0,
            headerFormatter: DataQualityWarningDisabledCellHeader(sortableHeaderFormatterWithSiteURLs),
            cellFormatter: null
          };
        }
        if (!this.server.catalogingOnSiteEnabled()) {
          return null;
        }
        // Right now createWarningColumnFormatter does not have any onlclick function
        return this.createLabelGridColumnFormatter('warning');

      case CommonColumn.LabelCertification:
        return this.createLabelGridColumnFormatter('certification');

      case CommonColumn.ContentType:
        return {
          name: CommonColumn.ContentType,
          dataKey: OrderKeys.contentTypeOrder,
          headerText: Messages.column_content_type(),
          resizable: false,
          sortable: this.params.showingMixedContent
            && !isRecentsSpace(this.params.spaceInfo)
            && !isSharedWithMeSpace(this.params.spaceInfo),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: ContentTypeCell({
            iconTypeForRow: this.contentTypeIconForRow,
            titleForIcon: this.contentTypeTitleForIconForRow,
            isCertified: this.isCertifiedDatasource,
            getTooltipProps: this.generateDSCertificationTooltipProps
          })
        };

      case CommonColumn.ContentTypeWithEmptyHeader:
        return {
          name: CommonColumn.ContentTypeWithEmptyHeader,
          dataKey: OrderKeys.contentTypeOrder,
          headerText: '',
          resizable: false,
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: ContentTypeCell({
            iconTypeForRow: this.contentTypeIconForRow,
            titleForIcon: this.contentTypeTitleForIconForRow,
            isCertified: this.isCertifiedDatasource,
            getTooltipProps: this.generateDSCertificationTooltipProps
          })
        };

      case CommonColumn.ContentTypeWithTableSubType:
        return {
          name: CommonColumn.ContentTypeWithTableSubType,
          dataKey: OrderKeys.contentTypeOrder,
          headerText: Messages.column_content_type(),
          resizable: true,
          preferredWidth: 160,
          sortable: this.params.showingMixedContent
            && !isRecentsSpace(this.params.spaceInfo)
            && !isSharedWithMeSpace(this.params.spaceInfo),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: ContentTypeTextCell({
            iconTypeForRow: this.contentTypeIconForRow,
            titleForIcon: this.contentTypeTitleForIconForRow,
            isCertified: this.isCertifiedDatasource,
            getTooltipProps: this.generateDSCertificationTooltipProps,
            contentType: this.contentTypeForRow
          })
        };

      case CommonColumn.Name:
        let nameColumn: DataGrid.Column = {
          name: CommonColumn.Name,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForName, true, this.params.urls)
        };
        return nameColumn;

      case CommonColumn.Category:
        let categoryColumn: DataGrid.Column = {
          name: CommonColumn.Category,
          dataKey: OrderKeys.name,
          sortable: false,
          headerText: Messages.column_category(),
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CategoryPopover()
        };
        return categoryColumn;

      case CommonColumn.Value:
          let valueColumn: DataGrid.Column = {
            name: CommonColumn.Value,
            dataKey: OrderKeys.value,
            sortable: false,
            headerText: Messages.column_value(),
            growToFit: true,
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: LabelValueCell('category','value','visiblity')
          };
          return valueColumn;

      case CommonColumn.Visibility:
          let visibilityColumn: DataGrid.Column = {
            name: CommonColumn.Visibility,
            dataKey: OrderKeys.visibility,
            sortable: false,
            headerText: Messages.column_visibility(),
            growToFit: true,
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: LabelVisibilityCell('category', 'visiblity')
          };
          return visibilityColumn;

       case CommonColumn.LabelDescription:
          let labelDescriptionColumn: DataGrid.Column = {
            name: CommonColumn.LabelDescription,
            dataKey: OrderKeys.labelDescription,
            sortable: false,
            headerText: Messages.column_labelDescription(),
            growToFit: true,
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: PlainTextCell('labelDescription')
          };
          return labelDescriptionColumn;

      case CommonColumn.OriginalViewName:
        let orignalViewNameColumn: DataGrid.Column = {
          name: CommonColumn.OriginalViewName,
          dataKey: OrderKeys.viewName,
          sortable: true,
          headerText: Messages.column_original_view_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForOriginalViewName)
        };
        return orignalViewNameColumn;

      case CommonColumn.NameWithMarkers:
        let nameColumnWithMarkers: DataGrid.Column = {
          name: CommonColumn.NameWithMarkers,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkWithDraftStatus(this.generateLinkForNameWithDraft)
        };
        return nameColumnWithMarkers;

      case CommonColumn.NameWithAccessStatus:
        return {
          name: CommonColumn.NameWithAccessStatus,
          dataKey: OrderKeys.name,
          sortable: !isSharedWithMeSpace(this.params.spaceInfo),
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCellWithAccessStatus(this.generateLinkForName)
        };

      case CommonColumn.ActionsMenu:
        return {
          name: CommonColumn.ActionsMenu,
          dataKey: 'actions',
          headerText: Messages.actions(),
          sortable: false,
          resizable: false,
          headerFormatter: sortableHeaderFormatterWithOverrides(CommonStyles.Layout.TextAlignCenter),
          cellFormatter: ActionsMenuCell(this.generateActionsOptions())
        };

      case CommonColumn.ActionsMenuForCatalog:
        const styleOverrides: React.CSSProperties = {
          ...CommonStyles.Layout.TextAlignCenter,
          paddingTop: 1, // add padding to align with catalog headers, which have slightly different format than VPC headers
        };
        return {
          name: CommonColumn.ActionsMenu,
          dataKey: 'actions',
          headerText: Messages.actions(),
          sortable: false,
          resizable: false,
          headerFormatter: sortableHeaderFormatterWithOverrides(styleOverrides),
          cellFormatter: ActionsMenuCell({
            ...this.generateActionsOptions(),
            getContentItem: (row: DataGrid.Row) => mapGraphTypeToContentInfoForActions(row.data as GraphDatabase | GraphTable),
          })
        };

      case CommonColumn.ProjectsCount:
        return {
          name: CommonColumn.ProjectsCount,
          dataKey: OrderKeys.childProjectCount,
          sortable: !isSingleCollectionSpace(this.params.spaceInfo),
          direction: 'desc',
          headerText: Messages.column_numChildren(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('childProjectCount'))
        };

      case CommonColumn.WorkbooksCount:
        return {
          name: CommonColumn.WorkbooksCount,
          dataKey: OrderKeys.workbookCount,
          sortable: !isSingleCollectionSpace(this.params.spaceInfo),
          direction: 'desc',
          headerText: Messages.column_numWorkbooks(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('workbookCount'))
        };

      case CommonColumn.DatasourceInWorkbooksCount:
        return {
          name: CommonColumn.DatasourceInWorkbooksCount,
          dataKey: OrderKeys.connectedWorkbooksCount,
          sortable: true,
          direction: 'desc',
          headerText: Messages.column_numWorkbooks(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LocalizedCountCellWithBar(
            (data: IDataSourceInfo) => data.connectedWorkbooksCount,
            (data: IDataSourceInfo) => data.usageInfoMaxValues && data.usageInfoMaxValues['connectedWorkbooksCount']
          )
        };

      case CommonColumn.LastMonthJobCount:
        return this.server.isOnlineEnvironment() ? undefined : {
          name: CommonColumn.LastMonthJobCount,
          dataKey: OrderKeys.lastMonthJobCount,
          headerText: Messages.lastMonthJobCount(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(PlainTextCell('lastMonthJobCount')),
        };

      case CommonColumn.lastMonthJobAvgTimeInSec:
        return this.server.isOnlineEnvironment() ? undefined : {
          name: CommonColumn.lastMonthJobAvgTimeInSec,
          dataKey: OrderKeys.lastMonthJobAvgTimeInSec,
          headerText: Messages.lastMonthAvgJobTimeInSec(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(LocalizedFloatCell(findLastMonthJobAvgTime)),
        };

      case CommonColumn.avgLoadTimeWithMaterialization:
        return {
          name: CommonColumn.avgLoadTimeWithMaterialization,
          dataKey: OrderKeys.avgLoadTimeWithMaterialization,
          headerText: Messages.avgLoadTimeWithMaterialization(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(PlainTextCell('avgLoadTimeWithMaterializationInSec')),
        };

      case CommonColumn.avgLoadTimeWithoutMaterialization:
        return {
          name: CommonColumn.avgLoadTimeWithoutMaterialization,
          dataKey: OrderKeys.avgLoadTimeWithoutMaterialization,
          headerText: Messages.avgLoadTimeWithoutMaterialization(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(PlainTextCell('avgLoadTimeWithoutMaterializationInSec')),
        };

      case CommonColumn.LastMonthViewCount:
        return {
          name: CommonColumn.LastMonthViewCount,
          dataKey: OrderKeys.lastMonthViewCount,
          headerText: Messages.lastMonthViewCount(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LocalizedCountCellWithBar(
            data => data.lastMonthViewCount,
            data => data.lastMonthViewCountMax)
        };

      case CommonColumn.AverageQueryTimeInSec:
        return {
          name: CommonColumn.AverageQueryTimeInSec,
          dataKey: OrderKeys.avgQueryBatchTime,
          headerText: Messages.avgQueryBatchTimeHeader(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(PlainTextCell('avgQueryBatchTime')),
        };

      case CommonColumn.RequestsToAccelerate:
        return {
          name: CommonColumn.RequestsToAccelerate,
          dataKey: OrderKeys.requestsToAccelerate,
          headerText: Messages.requestsToAccelerateHeader(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(PlainTextCell('requestsToAccelerate')),
        };

      case CommonColumn.EstimatedDiskUsageKb:
        return this.server.isOnlineEnvironment() ? undefined : {
          name: CommonColumn.EstimatedDiskUsageKb,
          dataKey: OrderKeys.estimatedDiskUsageKb,
          headerText: Messages.estimatedDiskUsageKb(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(LocalizedFloatCell(findEstimatedDiskUsage)),
        };

      case CommonColumn.Hits:
        const items: SelectableSortKeyColumnItem[] = _.map(SortOptions.hits.subSortOptions, sortOption => {
          return {
            dataKey: sortOption.field,
            headerText: sortOption.getButtonDisplayValue(),
            order: sortOption.toOrderClauseArray(),
            cellFormatter: LocalizedCountCellWithBar(
              (data: IItemStatisticsInfo<any>) => data.usageInfo && data.usageInfo[sortOption.field],
              (data: IItemStatisticsInfo<any>) => data.usageInfoMaxValues && data.usageInfoMaxValues[sortOption.field])
          };
        });

        let columnContext: SelectableSortKeyColumnContext = {
          currentSort: this.params.currentSort,
          changeSort: this.params.changeSort,
          setSortOrder: this.params.setSortOrder
        };
        let columnOptions: SelectableSortKeyColumnOptions = {
          columnName: CommonColumn.Hits,
          defaultDataKey: OrderKeys.hitsTotal,
          items: items
        };
        return SelectableSortKeyDataGridColumn(columnOptions, columnContext, true /* sortable */);

      case CommonColumn.ViewsCount:
        return {
          name: CommonColumn.ViewsCount,
          dataKey: OrderKeys.viewCount,
          sortable: !isSingleCollectionSpace(this.params.spaceInfo),
          direction: 'desc',
          headerText: Messages.column_numViews(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('viewCount'))
        };

      case CommonColumn.SheetsCount:
        return {
          name: CommonColumn.SheetsCount,
          dataKey: OrderKeys.numSheets,
          sortable: true,
          direction: 'desc',
          headerText: Messages.column_numSheets(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('numSheets'))
        } as any;

      case CommonColumn.Workbook:
        return {
          name: CommonColumn.Workbook,
          dataKey: OrderKeys.workbookName,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_workbook(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForWorkbook)
        };

      case CommonColumn.DatasourcesCount:
        return {
          name: CommonColumn.DatasourcesCount,
          dataKey: OrderKeys.datasourceCount,
          sortable: !isSingleCollectionSpace(this.params.spaceInfo),
          direction: 'desc',
          headerText: Messages.column_numDatasources(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('datasourceCount'))
        };

      case CommonColumn.DatabasesCount:
        // Do not show databases count column if cataloging is OFF on server or site, or databasesInProjectsPostMVP is disabled
        const showDatabasesCountColumn = this.server.catalogingOnServerEnabled() &&
        this.server.catalogingOnSiteEnabled() &&
        this.server.databasesInProjectsEnabled() &&
        this.server.databasesInProjectsPostMVPEnabled();
        if (!showDatabasesCountColumn) {
          return null;
        }
        return {
          name: CommonColumn.DatabasesCount,
          dataKey: OrderKeys.databasesCount,
          sortable: !isSingleCollectionSpace(this.params.spaceInfo),
          direction: 'desc',
          headerText: Messages.column_numDatabases(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('databaseCount'))
        };

      case CommonColumn.TablesCount:
        const showTablesCountColumn = getSessionManagementServiceInstance()
                                        .tablesInProjectsUIEnabled();
        if(!showTablesCountColumn){
          return;
        }
        return {
          name: CommonColumn.TablesCount,
          dataKey: OrderKeys.tablesCount,
          sortable: !isSingleCollectionSpace(this.params.spaceInfo),
          direction: 'desc',
          headerText: Messages.column_numTables(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('tableCount'))
        };
      case CommonColumn.Size:
        return {
          name: CommonColumn.Size,
          dataKey: OrderKeys.size,
          sortable: true,
          headerText: Messages.column_size(),
          direction: 'desc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(SizeCell('size'))
        };

      case CommonColumn.SheetNumber:
        return {
          name: CommonColumn.SheetNumber,
          dataKey: OrderKeys.index,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_sheetNumber(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('index'))
        };

      case CommonColumn.ConnectsTo:
        const column = {
          name: CommonColumn.ConnectsTo,
          dataKey: 'connectsTo',
          headerText: Messages.column_connects_to(),
          sortable: false,
          growToFit: this.showingOnlyDataConnections(),
          headerFormatter: sortableHeaderFormatter
        };

        const getPopoverContent = ConnectionHoverPanel.getPopoverContent(this.params.urls);
        let cellFormatter = ConnectsToCell.cellFormatter(this.params.urls, getPopoverContent, ConnectionHoverPanel.shouldDisplayPanel);
        if (FeatureFlags.datasourceCertificationEnabled && isWorkbookSpace(this.params.spaceInfo)) {
          cellFormatter = CertifiableConnectsToCell.cellFormatter(
            this.isCertifiedDatasource, this.generateDSCertificationTooltipProps, this.params.urls,
            getPopoverContent, ConnectionHoverPanel.shouldDisplayPanel);
        }

        if (this.showingOnlyDataConnections()) {
          // We can't use the StyledCellFormatter because it makes it so you have to click twice for a link to work
          // so instead we add the left padding here
          const leftPaddingStyle = { paddingLeft: CommonStyles.Sizes.MediumSpace };
          const connectsToColumn = { ...column, cellFormatter: StyledCellFormatter(cellFormatter, leftPaddingStyle) };
          return connectsToColumn;
        } else {
          const connectsTocolumn = { ...column, cellFormatter: StyledCellFormatter(cellFormatter) };
          return connectsTocolumn;
        }

      case CommonColumn.Location:
        return {
          name: CommonColumn.Location,
          dataKey: this.getOrderKeysBasedOnContentType(),
          sortable: true,
          headerText: Messages.column_location(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForLocation)
        };

      case CommonColumn.Owner:
        return {
          name: CommonColumn.Owner,
          dataKey: OrderKeys.ownerName,
          sortable: !isSharedWithMeSpace(this.params.spaceInfo),
          headerText: Messages.column_owner(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForOwner),
          ...(isCollectionsSpace(this.params.spaceInfo) && { preferredWidth: 130 }),
        };

      case CommonColumn.OwnerWithIcon:
        return {
          name: CommonColumn.OwnerWithIcon,
          dataKey: OrderKeys.ownerName,
          sortable: !isCollectionsSpace(this.params.spaceInfo),
          headerText: Messages.column_owner(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForOwnerWithIcon)
        };

      case CommonColumn.Created:
        return {
          name: CommonColumn.Created,
          dataKey: OrderKeys.createdAt,
          sortable: true,
          headerText: Messages.column_created(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DateTimeCell('createdAt')
        };

      // Corresponds to sort=createdTime in Collections REST API
      // This is backed by ICreatedItemInfo.createdAt field
      case CommonColumn.CreatedTime:
        return {
          name: CommonColumn.CreatedTime,
          dataKey: OrderKeys.createdTime,
          sortable: true,
          headerText: Messages.column_created(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DateTimeCell('createdAt')
        };

      case CommonColumn.LastAccessedAt:
        return {
          name: CommonColumn.LastAccessedAt,
          dataKey: OrderKeys.lastAccessedAt,
          sortable: true,
          headerText: Messages.column_lastAccessedAt(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DateTimeCell('lastAccessedAt')
        };

      case CommonColumn.LastRefreshedAt:
        return {
          name: CommonColumn.LastRefreshedAt,
          dataKey: OrderKeys.lastRefreshedAt,
          sortable: true,
          headerText: Messages.column_lastRefreshedAt(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DateTimeCell('lastRefreshedAt')
        };

      case CommonColumn.Modified:
        const modifiedFormatter = (props: BodyCell.Props) => {
          return this.invalidForModified(props.row)? FixedValueCell('')(props): DateTimeCell('updatedAt')(props);
        };

        return {
          name: CommonColumn.Modified,
          dataKey: OrderKeys.updatedAt,
          sortable: true,
          headerText: Messages.column_updated(),
          direction: 'desc',
          resizable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: modifiedFormatter
        };

      // Corresponds to sort=updatedTime in Collections REST API
      // This is backed by IUpdatedItemInfo.updatedAt field
      case CommonColumn.UpdatedTime:
        return {
          name: CommonColumn.UpdatedTime,
          dataKey: OrderKeys.updatedTime,
          sortable: true,
          headerText: Messages.column_updated(),
          direction: 'desc',
          resizable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DateTimeCell('updatedAt')
        };

      case CommonColumn.ModifiedBy:
        return {
          name: CommonColumn.ModifiedBy,
          dataKey: OrderKeys.modifiedBy,
          sortable: true,
          headerText: Messages.column_modified_by(),
          direction: 'asc',
          resizable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForModifiedBy)
        };

      case CommonColumn.ExtractStatusOnly:
        return {
          name: CommonColumn.ExtractStatusOnly,
          dataKey: OrderKeys.lastRefreshedAt,
          headerText: Messages.extractStatusLabelTitle(),
          sortable: true,
          resizable: true,
          direction: 'asc',
          headerFormatter: SortableHeader.Formatter(this.params.currentSort, this.params.changeSort, showEncryptionTooltip ?
            <EncryptionTooltip /> : null),
          cellFormatter: LastRefreshedCell(this.params.urls, this.server.isExtractEncryptionEnabledOmniSwitch(), this.server.getExtractEncryptionModeForSite(), true)
        };

      case CommonColumn.ExtractStatus: {
        const dateColumnSpec: SelectableSortKeyColumnOptions = {
          columnName: CommonColumn.ExtractStatus,
          defaultDataKey: OrderKeys.lastRefreshedAt,
          items: [
            {
              dataKey: OrderKeys.lastRefreshedAt,
              headerText: Messages.extractStatusLabel(),
              order: [{ field: OrderKeys.lastRefreshedAt, ascending: false }],
              cellFormatter: LastRefreshedCell(this.params.urls, this.server.isExtractEncryptionEnabledOmniSwitch(), this.server.getExtractEncryptionModeForSite()),
              tooltip: showEncryptionTooltip ? EncryptionTooltip : null,
            },
            {
              dataKey: OrderKeys.updatedAt,
              headerText: Messages.column_updated(),
              order: [{ field: OrderKeys.updatedAt, ascending: false }],
              cellFormatter: DateTimeCell('updatedAt')
            }
          ]
        };

        const extractColumnContext: SelectableSortKeyColumnContext = {
          currentSort: this.params.currentSort,
          changeSort: this.params.changeSort,
          setSortOrder: this.params.setSortOrder,
        };
        // TODO: TFSID: 1246324 - jwonglam - Disabling sorting on SingleCollection Space because the sort from CRASS doesn't return right order
        return SelectableSortKeyDataGridColumn(dateColumnSpec, extractColumnContext, !isSingleCollectionSpace(this.params.spaceInfo) /*sortable*/);
      }

      case CommonColumn.Alert:
        return {
          name: CommonColumn.Alert,
          dataKey: OrderKeys.hasAlert,
          sortable: true,
          headerText: '',
          direction: 'desc',
          resizable: false,
          headerTooltip: Messages.column_has_alert(),
          headerFormatter: AlertCellHeader(sortableHeaderFormatter),
          cellFormatter: AlertCell(this.generateAlert)
        };

      case CommonColumn.ConnectionType:
        return {
          name: CommonColumn.ConnectionType,
          dataKey: OrderKeys.connectionTypeDisplayName,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_connection_type(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCell(OrderKeys.connectionTypeDisplayName)
        };
        case CommonColumn.TableConnectionType:
          return {
            name: CommonColumn.TableConnectionType,
            dataKey: OrderKeys.connectionTypeDisplayName,
            sortable: false,
            direction: 'asc',
            headerText: Messages.column_connection_type(),
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: PlainTextCellWithNestedValueAndTransform('database', 'connectionType', messageForConnectionType)
          };
      case CommonColumn.ConnectionTypeInCrass:
        return {
          name: CommonColumn.ConnectionTypeInCrass,
          dataKey: OrderKeys.connectionTypeDisplayNameCrass,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_connection_type(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform(OrderKeys.connectionTypeDisplayNameCrass, messageForConnectionType)
        };

      case CommonColumn.Contact:
        return {
          name: CommonColumn.Contact,
          dataKey: 'contact',
          sortable: false,
          headerText: Messages.column_contact(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCell('contact')
        };
      case CommonColumn.ServerOrPath:
        return {
          name: CommonColumn.ServerOrPath,
          dataKey: 'hostName',
          sortable: false,
          headerText: Messages.column_server_or_path(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCell('hostName')
        };
      case CommonColumn.DownstreamDataSourcesCount:
        return {
          name: CommonColumn.DownstreamDataSourcesCount,
          dataKey: OrderKeys.downstreamDataSourceCount,
          sortable: true,
          headerText: Messages.column_downstream_data_source_counts(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LocalizedCountCellWithBar(
            ({ downstreamDatasourcesConnection }) => downstreamDatasourcesConnection ? downstreamDatasourcesConnection.totalCount : null,
            ({ maxDownstreamDatasources }) => maxDownstreamDatasources ? maxDownstreamDatasources : null
          )
        };
      case CommonColumn.DownstreamWorkbookCount:
        return {
          name: CommonColumn.DownstreamWorkbookCount,
          dataKey: OrderKeys.downstreamWorkbookCount,
          sortable: true,
          headerText: Messages.column_downstream_workbook_counts(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LocalizedCountCellWithBar(
            ({ downstreamWorkbooksConnection }) => downstreamWorkbooksConnection ? downstreamWorkbooksConnection.totalCount : null,
            ({ maxDownstreamWorkbooks }) => maxDownstreamWorkbooks ? maxDownstreamWorkbooks : null
          )
        };
      case CommonColumn.TableDatabase:
        return {
          name: CommonColumn.TableDatabase,
          dataKey: OrderKeys.tableDatabase,
          sortable: false,
          headerText: Messages.column_table_database(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithNestedValue('database','name')
        };
      case CommonColumn.TableColumns:
        return {
          name: CommonColumn.TableColumns,
          dataKey: OrderKeys.tableColumns,
          sortable: false,
          headerText: Messages.column_table_columns(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCell('columns')
        };
      case CommonColumn.TypeForCrass:
        return {
          name: CommonColumn.TypeForCrass,
          dataKey: OrderKeys.typeForCrass,
          sortable: false,
          headerText: Messages.type(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCell(OrderKeys.typeForCrass)
        };
      case CommonColumn.Authentication:
        return {
          name: CommonColumn.Authentication,
          dataKey: 'authentication',
          sortable: false,
          headerText: Messages.authentication(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataConnectionDataGridCells.AuthenticationCellFormatter()
        };

      case CommonColumn.Username:
        return {
          name: CommonColumn.Username,
          dataKey: 'username',
          sortable: false,
          headerText: Messages.username(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataConnectionDataGridCells.UsernameCellFormatter()
        };
      case CommonColumn.IdentityConflictFailureType:
        return {
          name: CommonColumn.IdentityConflictFailureType,
          dataKey: OrderKeys.failureType,
          sortable: true,
          headerText: Messages.identityMigration_FailureType_ColumnHeader(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform('failureType', localizeIdentityMigrationFailureType)
        };
      case CommonColumn.Datasource:
        return {
          name: CommonColumn.Datasource,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_datasource(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForDataconnectionDatasource)
        };

      case CommonColumn.Subject:
        return {
          name: CommonColumn.Subject,
          dataKey: OrderKeys.subject,
          growToFit: isScheduleSpace(this.params.spaceInfo),
          sortable: true,
          headerText: Messages.column_task_subject(),
          headerFormatter: sortableHeaderFormatter,
          direction: 'asc',
          cellFormatter: PlainTextCell(OrderKeys.subject),
        };

      case CommonColumn.UsernameForSubscription:
        const userNameColumn = {
          name: CommonColumn.UsernameForSubscription,
          dataKey: OrderKeys.userDisplayName,
          sortable: true,
          headerText: Messages.column_task_user(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell((row: DataGrid.Row) => {
            const subscription = row.data as ISubscriptionInfo;
            return {
              text: subscription.user.displayName,
              url: new SpacesSiteUrls({ siteUrlName: subscription.site.urlName })
                .userUrl(subscription.user.username, subscription.user.domainName),
              iconClassName: 'tb-grid-icon-user',
              iconTitle: Messages.user()
            };
          })
        };

        return userNameColumn;

      case CommonColumn.SubscribedTo:
        const subscribedToColumn = {
          name: CommonColumn.SubscribedTo,
          dataKey: OrderKeys.targetName,
          sortable: true,
          headerText: Messages.column_task_subscription(),
          direction: 'asc',
          growToFit: isSiteUserSpace(this.params.spaceInfo),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell((row: DataGrid.Row) => {
            const subscription = row.data as ISubscriptionInfo;
            return SubscriptionGrid.getTargetLinkMetadata(subscription);
          })
        };
        return subscribedToColumn;

      case CommonColumn.Schedule:
        return {
          name: CommonColumn.Schedule,
          dataKey: OrderKeys.scheduleName,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_task_schedule(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: ScheduleDescriptionCellWithIcon(
            (row: DataGrid.Row) => {
              const subscription = row.data as ISubscriptionInfo;
              return subscription.canSpecifyDataCondition && subscription.dataConditionPresent;
            },
            this.params.isSessionSiteOrServerAdmin ?
              (row: DataGrid.Row) => {
                const subscription = row.data as ISubscriptionInfo;
                const schedule = subscription.schedule;
                //TODO: MAKE THIS WORK FOR THE SERVER PAGE
                return siteScheduleUrl(new SpacesSiteUrls({ siteUrlName: subscription.site.urlName }), schedule.id, schedule.scheduledAction);
              }
              : null,
            subscriptionOnExtractRefreshFn,
            (row: DataGrid.Row) => {
              const nonScheduledAcceleration = row.data.schedule === undefined;
              return FeatureFlags.accelerationSiteAdminUIEnabled && nonScheduledAcceleration;
            })
        };

      case CommonColumn.SubscribedBy:
        return {
          name: CommonColumn.SubscribedBy,
          dataKey: OrderKeys.creatorName,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_task_subscribed_by(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForSubscribedBy),
        };

      case CommonColumn.LastUpdate:
        if (FeatureFlags.suspendSubscriptions) {
          const lastUpdateCell = StyledCellFormatter(InteractiveIconTooltipCell((row: DataGrid.Row) => {
            const subscription = row.data as ISubscriptionInfo;
            const dateTime = subscription.lastSentAt;
            const dateTimeText = dateTime ? Formatters.formatMediumDateTimeWithoutSeconds(dateTime) : Messages.never();
            if (subscription.suspended) {
              // edge case: subscription was suspended but there is no last sent at date
              const subscriptionMessage = dateTime ? Messages.subscriptionSuspended({ SUSPENDED_DATE: dateTimeText }) : Messages.suspended();
              return {
                text: subscriptionMessage,
                title: Messages.subscriptionTooltipTitle(),
                iconClass: 'tb-react-dg-hcol tb-icon-server-alert-list-suspended',
                tooltipText: Messages.subscriptionSuspendedMessage(),
                tooltipReplacements: { ERROR_MESSAGE: () => subscription.lastRunError ? subscription.lastRunError : '' },
                tooltipLinkText: Messages.learnMore(),
                tooltipLink: HelpService.getServerHelpUrlByCategory('sub-suspend')
              };
            } else if (subscription.failed) {
              return {
                text: Messages.subscriptionFailed({ FAILED_DATE: dateTimeText }),
                title: Messages.subscriptionTooltipTitle(),
                iconClass: 'tb-react-dg-hcol tb-icon-dialogs-status-alert',
                tooltipText: Messages.subscriptionFailedMessage(),
                tooltipReplacements: { ERROR_MESSAGE: () => subscription.lastRunError ? subscription.lastRunError : '' },
                tooltipLinkText: Messages.learnMore(),
                tooltipLink: HelpService.getServerHelpUrlByCategory(subscription.targetType === 'flow' ? 'prep-conductor-subscribe-flow-output' : 'sub-suspend')
              };
            } else {
              // a non-failed, non-suspended subscription
              return {
                text: dateTimeText,
                title: Messages.subscriptionTooltipTitle()
              };
            }
          }));

          return {
            name: CommonColumn.LastUpdate,
            dataKey: OrderKeys.lastSentAt,
            sortable: true,
            headerText: Messages.column_task_last_update(),
            direction: 'desc',
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: lastUpdateCell
          };
        } else {
          return {
            name: CommonColumn.LastUpdate,
            dataKey: OrderKeys.lastSentAt,
            sortable: true,
            headerText: Messages.column_task_last_update(),
            direction: 'desc',
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: DateTimeCell('lastSentAt', Messages.never())
          };
        }

      case CommonColumn.LastRunAt:
        return {
          name: CommonColumn.LastRunAt,
          dataKey: OrderKeys.lastRunAt,
          sortable: true,
          headerText: Messages.column_task_last_update(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DateTimeCell('lastRunAt', Messages.never())
        };

      case CommonColumn.LastRunAtExtractRefreshes:
        return this.getLastRunAtExtractRefreshes(sortableHeaderFormatter);
      case CommonColumn.RunNextAtSubscription:
        return {
          name: CommonColumn.RunNextAt,
          dataKey: OrderKeys.runNextAt,
          sortable: true,
          headerText: Messages.column_task_next_update(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: SubscriptionNextUpdateDescriptionCell()
        };

      case CommonColumn.LinkedTaskWithCount:
        return {
          name: CommonColumn.LinkedTaskWithCount,
          dataKey: 'totalSteps',
          sortable: false,
          headerText: Messages.column_schedule_linked_task(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkedTaskWithCountCell()
        };

      case CommonColumn.RunNextAt:
        return {
          name: CommonColumn.RunNextAt,
          dataKey: OrderKeys.runNextAt,
          sortable: true,
          headerText: Messages.column_task_next_update(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DateTimeCell('runNextAt', Messages.disabled())
        };

      case CommonColumn.TaskType:
        const taskColumn = {
          name: CommonColumn.TaskType,
          dataKey: OrderKeys.type,
          sortable: true,
          headerText: Messages.column_task_type(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform('type',
            (type: string) => (type === ExtractTaskType.IncrementExtract || type === ExtractTaskType.ExtractServiceIncrement)
              ? Messages.extractTasksIncremental()
              : Messages.extractTasksFull())
        };

        return taskColumn;

      case CommonColumn.RemoteRefreshType:
        const remoteRefreshColumn = {
          name: CommonColumn.RemoteRefreshType,
          dataKey: OrderKeys.refreshType,
          sortable: true,
          headerText: Messages.column_task_type(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform('refreshType',
            (refreshType: string) => refreshType === RemoteRefreshTypes.Incremental
              ? Messages.remoteRefreshScheduleTypeNameIncremental()
              : Messages.remoteRefreshScheduleTypeNameFull())
        };
        return remoteRefreshColumn;

      case CommonColumn.Priority:
        return {
          name: CommonColumn.Priority,
          dataKey: OrderKeys.priority,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_task_priority(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(LocalizedCountCell('priority'))
        };

      case CommonColumn.RemoteRefreshPriority:
        return {
          name: CommonColumn.RemoteRefreshPriority,
          dataKey: OrderKeys.priority,
          sortable: false,
          headerText: Messages.column_task_priority(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: FixedValueCell(Messages.notApplicable()),
        };

      case CommonColumn.Definition:
        return {
          name: CommonColumn.Definition,
          dataKey: OrderKeys.definition,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_remote_schedule_definition(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: ScheduleFrequencyDescriptionCell(),
        };

      case CommonColumn.OutputStep:
        return {
          name: 'output-col',
          dataKey: OrderKeys.flowOutputStepName,
          headerText: Messages.flowRunHistoryColumn_output(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          direction: 'asc',
          cellFormatter: PlainTextCell('outputName')
        };

      case CommonColumn.OutputSteps:
        return {
          name: CommonColumn.OutputSteps,
          dataKey: OrderKeys.flowOutputSteps,
          headerText: Messages.outputStepsColumnName(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          direction: 'asc',
          cellFormatter: this.params.inServerArea ? SchedulesGridCells.ServerOutputsCell()
            : FlowTaskOutputStepsWithIconsCell()
        };

      case CommonColumn.OutputStepsWithLinkedTasks:
        return {
          name: CommonColumn.OutputStepsWithLinkedTasks,
          dataKey: OrderKeys.flowOutputSteps,
          headerText: Messages.outputStepsColumnName(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          direction: 'asc',
          cellFormatter: this.params.inServerArea ? SchedulesGridCells.ServerOutputsCell() : FlowOrLinkedTaskOutputStepsWithIconsCell(this.params.urls)
        };
      case CommonColumn.FlowName:
        const flowTaskNameColumn = {
          name: CommonColumn.FlowName,
          dataKey: OrderKeys.flowName,
          headerText: Messages.flow(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          direction: 'asc',
          cellFormatter: LinkCell(this.generateLinkForTasksFlowName)
        };
        return flowTaskNameColumn;

      case CommonColumn.FlowOrLinkedTask:
        const flowOrLinkedTaskColumn = {
          name: CommonColumn.FlowOrLinkedTask,
          dataKey: OrderKeys.flowName,
          headerText: Messages.flow(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: areLinkedTasksEnabled() ? RunFlowTaskCell(this.params.urls) : LinkCell(this.generateLinkForTasksFlowName)
        };
        return flowOrLinkedTaskColumn;

      case CommonColumn.FlowFileType:
        return {
          name: CommonColumn.FlowFileType,
          dataKey: OrderKeys.flowFileType,
          sortable: false,
          headerText: Messages.column_flow_file_type(),
          headerFormatter: sortableHeaderFormatter,
          direction: 'asc',
          cellFormatter: PlainTextCell(OrderKeys.flowFileType),
        };

      case CommonColumn.FlowScheduleType:
        return {
          name: CommonColumn.FlowScheduleType,
          dataKey: OrderKeys.flowScheduleType,
          sortable: false,
          headerText: Messages.linkedTasksScheduleTypeColumnHeader(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: FlowScheduleTypeCell(),
        };

      case CommonColumn.FlowParameterWithOverride:
        return {
          name: CommonColumn.FlowParameterWithOverride,
          dataKey: OrderKeys.flowParameters,
          sortable: false,
          headerText: Messages.flowParametersHeader(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: FlowParameterWithOverrideCell(),
        };

      case CommonColumn.FlowParameterRunWithOverride:
        return {
          name: CommonColumn.FlowParameterRunWithOverride,
          dataKey: OrderKeys.flowParameters,
          sortable: false,
          headerText: Messages.flowParametersHeader(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: FlowParameterRunWithOverrideCell(),
        };

      case CommonColumn.RunFlowTasksStatus:
        return {
          name: CommonColumn.RunFlowTasksStatus,
          dataKey: OrderKeys.status,
          headerText: Messages.flowRunHistoryColumn_status(),
          sortable: false,
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: FlowTaskStatusWithIconsCell(this.params.urls, this.params.flowFailureThresholdForRunPrevention)
        };

      case CommonColumn.LastRunAtFlows:
        return {
          name: CommonColumn.LastRunAtFlows,
          dataKey: OrderKeys.lastRunAt,
          sortable: false,
          headerText: Messages.lastRun(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DateTimeCell('lastRunAt', Messages.never())
        };

      case CommonColumn.RunNextAtFlows:
        return {
          name: CommonColumn.RunNextAtFlows,
          dataKey: OrderKeys.runNextAt,
          sortable: true,
          headerText: Messages.nextScheduledRun(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DateTimeCell('runNextAt', Messages.disabled())
        };

      case CommonColumn.RunType:
        return {
          name: 'schedule-col',
          dataKey: 'schedule',
          headerText: Messages.flowRunHistoryColumn_runTypeTitle(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          direction: 'asc',
          cellFormatter: FeatureFlags.incrementalRefresh ? FlowRefreshTypeCell() : FlowRunTypeCell()
        };

      case CommonColumn.RunStartedAt:
        return {
          name: 'start-col',
          dataKey: OrderKeys.startedAt,
          headerText: Messages.flowRunHistoryColumn_runStart(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          direction: 'desc',
          cellFormatter: DateTimeCell('startedAt')
        };

      case CommonColumn.RunEndedAt:
        return {
          name: 'end-col',
          dataKey: OrderKeys.completedAt,
          headerText: Messages.flowRunHistoryColumn_runEnd(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          direction: 'desc',
          cellFormatter: DateTimeCell('completedAt')
        };

      case CommonColumn.RunDuration:
        return {
          name: 'duration-col',
          dataKey: 'duration',
          headerText: Messages.flowRunHistoryColumn_duration(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: FlowRunDurationCell('startedAt', 'completedAt'),
        };

      case CommonColumn.RunStatus:
        return {
          name: 'status-col',
          dataKey: OrderKeys.status,
          headerText: Messages.flowRunHistoryColumn_status(),
          sortable: true,
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithIcon((row: any) => {
            const rowData = row as FlowOutputStepRunListItemInfo;
            return {
              text: localizeFlowRunHistoryStatus(rowData.status),
              iconClass: getFlowOutputStepRunIconClass(rowData.status)
            };
          })
        };

      case CommonColumn.RunRowsProduced:
        return {
          name: 'rows-produced-col',
          dataKey: OrderKeys.rowsProduced,
          headerText: Messages.flowRunHistoryColumn_rowsGeneratedTitle(),
          sortable: true,
          direction: 'desc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('rowsProduced')),
        };

      case CommonColumn.RunErrors:
        const siteUrls = new SiteUrls({
          siteUrlName: this.server.sessionSiteUrlName(),
          queryString: window.location.search,
        });
        return {
          name: CommonColumn.RunErrors,
          dataKey: 'errors',
          headerText: Messages.flowRunHistoryColumn_errors(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: FlowRunHistoryErrorsCell(siteUrls, true),
        };

      case CommonColumn.Id:
        const idFormatter = (props: BodyCell.Props) => {
          const backgroundJob = props.row.data as IBackgroundJobInfo;
          return <BackgroundJobExtendedInfoDialogCell {...backgroundJob} />;
        };
        return {
          name: 'id-col',
          dataKey: OrderKeys.jobId,
          headerText: Messages.column_backgroundJob_id(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          // We avoid using factories as they are deprecated
          cellFormatter: idFormatter as React.Factory<any>,
        };
      case CommonColumn.Status:
        const statusFormatter = InteractiveImageIconTooltipCell((row: DataGrid.Row) => {
          const backgroundJob = row.data as unknown as IBackgroundJob;
          return {
            text: localizeBackgroundJobState(backgroundJob),
            title: Messages.subscriptionTooltipTitle(),
            iconClass: 'tb-react-dg-hcol',
            iconUrl: getIconForBackgroundJob(backgroundJob),
            iconTitle: localizeBackgroundJobState(backgroundJob),
            tooltipText: getBackgroundJobStatusTooltip(backgroundJob),
            tooltipLinkText: Messages.learnMore(),
            tooltipLink: HelpService.getServerHelpUrlByCategory('jobmgmt')
          };
        });
        return {
          name: 'status-col',
          dataKey: OrderKeys.status,
          headerText: Messages.column_backgroundJob_status(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: statusFormatter,
          preferredWidth: 120,
        };
      case CommonColumn.CurrentRunTime:
        return {
          name: 'runtime-col',
          dataKey: OrderKeys.currentRunTime,
          headerText: Messages.column_backgroundJob_runTime(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(LocalizedFloatCell((job => (job[OrderKeys.currentRunTime] / 60)))),
        };
      case CommonColumn.CurrentQueueTime:
        return {
          name: 'queuetime-col',
          dataKey: OrderKeys.currentQueueTime,
          headerText: Messages.column_backgroundJob_queueTime(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(LocalizedFloatCell((job => (job[OrderKeys.currentQueueTime] / 60)))),
        };
      case CommonColumn.AverageRunTime:
        return {
          name: 'average-runtime-col',
          headerText: Messages.column_backgroundJob_averageRunTime(),
          dataKey: OrderKeys.averageRunTime,
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(LocalizedFloatCell((job => (job[OrderKeys.averageRunTime] / 60)))),
          preferredWidth: 110,
        };
      case CommonColumn.AverageQueueTime:
        return {
          name: 'average-queuetime-col',
          headerText: Messages.column_backgroundJob_averageQueueTime(),
          dataKey: OrderKeys.averageQueueTime,
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(LocalizedFloatCell((job => (job[OrderKeys.averageQueueTime] / 60)))),
          preferredWidth: 125,
        };
      case CommonColumn.BackgroundJobType:
        return {
          name: 'taskType-col',
          dataKey: OrderKeys.taskType,
          headerText: Messages.column_backgroundJob_taskType(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform(OrderKeys.taskType, localizeBackgroundJobTaskType),
        };
      case CommonColumn.TargetType:
        return {
          name: CommonColumn.TargetType,
          dataKey: OrderKeys.targetType,
          headerText: Messages.column_content_type(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          resizable: false,
          cellFormatter: ContentTypeCell({
            iconTypeForRow: this.targetContentTypeIconForRow,
            titleForIcon: this.targetContentTypeTitleForRow,
            isCertified: () => false,
            getTooltipProps: () => null,
          })
        };
      case CommonColumn.AccelerationTaskName:
        const accelerationTaskNameColumn = {
          name: CommonColumn.AccelerationTaskName,
          dataKey: OrderKeys.targetName,
          growToFit: true,
          headerText: Messages.accelerationViewHeader(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForAccelerationTaskName),
        };
        return accelerationTaskNameColumn;
      case CommonColumn.ExtractTaskName:
        const extractTaskNameColumn = {
          name: CommonColumn.ExtractTaskName,
          dataKey: OrderKeys.targetName,
          growToFit: isScheduleSpace(this.params.spaceInfo),
          headerText: Messages.column_name(),
          sortable: true,
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForExtractTaskName, true),
        };
        return extractTaskNameColumn;
      case CommonColumn.WorkbookDatasourceName:
        const workbookDatasourceNameColumn = {
          name: CommonColumn.WorkbookDatasourceName,
          dataKey: OrderKeys.targetName,
          growToFit: isScheduleSpace(this.params.spaceInfo),
          headerText: Messages.column_task_workbook_data_source(),
          sortable: true,
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForWorkbookDatasourceName),
        };
        return workbookDatasourceNameColumn;
      case CommonColumn.MetricValue:
        return {
          name: CommonColumn.MetricValue,
          dataKey: OrderKeys.metricValue,
          headerText: Messages.column_metrics_value(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: MetricValueCell(),
        };
      case CommonColumn.MetricConnectedView:
        return {
          name: CommonColumn.MetricConnectedView,
          dataKey: OrderKeys.connectedViewName,
          headerText: Messages.column_metrics_viewName(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForMetricConnectedView),
        };
      case CommonColumn.MetricUpdated:
        return {
          name: CommonColumn.MetricUpdated,
          dataKey: OrderKeys.metricUpdated,
          headerText: Messages.column_metrics_lastUpdated(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: MetricUpdatedCell(),
          // TFSID: 1039542 - 174px is just wide enough to fit the "Refresh Suspended" text.
          preferredWidth: 174,
        };
      case CommonColumn.BackgroundJobPriority:
        return {
          name: CommonColumn.Priority,
          dataKey: OrderKeys.priority,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_task_priority(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: RightAlignedCell(LocalizedCountCell(OrderKeys.priority))
        };
      case CommonColumn.JobRequestedTime:
        return {
          name: CommonColumn.JobRequestedTime,
          dataKey: OrderKeys.jobRequestedTime,
          sortable: true,
          headerText: Messages.column_backgroundJob_jobRequestedTime(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DateTimeCell(OrderKeys.jobRequestedTime, Messages.never())
        };
      case CommonColumn.DataAlertSubject:
        const subjectColumn = {
          name: CommonColumn.DataAlertSubject,
          dataKey: OrderKeys.title,
          sortable: true,
          direction: 'asc',
          growToFit: true,
          headerText: Messages.column_task_subject(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCell('name')
        };
        return subjectColumn;
      case CommonColumn.DataAlertView:
        return {
          name: CommonColumn.DataAlertView,
          dataKey: OrderKeys.targetName,
          sortable: true,
          headerText: Messages.column_view_header(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell((row: DataGrid.Row) => {
            const data = row.data as IDataAlertInfo;
            data.deviceType = data.deviceType.toString().toLowerCase() as DeviceType;
            return {
              text: DataAlertDataGridCells.getViewName(data),
              url: DataAlertDataGridCells.getViewUrl(data, this.params.urls),
              iconClassName: 'tb-grid-icon-view',
              iconTitle: Messages.view()
            };
          })
        };
      case CommonColumn.DataAlertRecepient:
        const numRecipientsShown = 2;
        return {
          name: CommonColumn.DataAlertRecepient,
          dataKey: 'displayRecipients',
          sortable: false,
          headerText: Messages.column_recipients(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataAlertDataGridCells.RecipientsListCell({
            numRecipientsShown,
            siteUrls: this.params.urls,
            server: this.server,
            onPillClick: (dataAlert: IDataAlertInfo) => {
              ManageDataAlertRecipientsDialog.instance.open(dataAlert);
            },
            testId: 'dda-recipients-cell'
          })
        };
      case CommonColumn.DataAlertVisibleTo:
        return {
          name: CommonColumn.DataAlertVisibleTo,
          dataKey: 'isPublic',
          sortable: false,
          headerText: Messages.column_alertPrivacy(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform('isPublic', (isPublic: boolean) => isPublic ? Messages.dataAlertPublic() : Messages.dataAlertPrivate())
        };
      case CommonColumn.DataAlertFrequency:
        const mapNotificationInterval = DataAlertDataGridCells.createMapNotificationInterval();
        return {
          name: CommonColumn.DataAlertFrequency,
          dataKey: OrderKeys.notificationIntervalInMinutes,
          sortable: true,
          headerText: Messages.column_notificationFrequency(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform('notificationIntervalInMinutes', mapNotificationInterval)
        };
      case CommonColumn.DataAlertLastCheck:
        return {
          name: CommonColumn.DataAlertLastCheck,
          dataKey: OrderKeys.lastChecked,
          sortable: true,
          headerText: Messages.column_last_checked(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataAlertDataGridCells.buildLastCheckedFormatter()
        };
      case CommonColumn.DataAlertLast:
        return {
          name: CommonColumn.DataAlertLast,
          dataKey: 'lastAlerted',
          sortable: false,
          headerText: Messages.column_last_alerted(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataAlertDataGridCells.LastAlertCell(Messages.never())
        };
      case CommonColumn.SiteNameLink:
        let siteNameColumn: DataGrid.Column = {
          name: CommonColumn.SiteNameLink,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForSiteName)
        };
        return siteNameColumn;
      case CommonColumn.SiteUserCount:
        return {
          name: CommonColumn.SiteUserCount,
          dataKey: OrderKeys.userCount,
          sortable: true,
          headerText: Messages.column_userCount(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('userCount'))
        };
      case CommonColumn.SiteAdminCount:
        return {
          name: CommonColumn.SiteAdminCount,
          dataKey: OrderKeys.adminCount,
          sortable: true,
          headerText: Messages.column_adminCount(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('adminCount'))
        };
      case CommonColumn.SiteUserQuota:
        return {
          name: CommonColumn.SiteUserQuota,
          dataKey: OrderKeys.userQuota,
          sortable: true,
          headerText: Messages.column_maxUsers(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(SiteGridCells.userQuotaFormatter())
        };
      case CommonColumn.SiteStorageUsed:
        return {
          name: CommonColumn.SiteStorageUsed,
          dataKey: OrderKeys.storageUsedBytes,
          sortable: true,
          headerText: Messages.column_storageUsed(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(SiteGridCells.StorageUsedFormatter)
        };
      case CommonColumn.SiteStorageQuota:
        return {
          name: CommonColumn.SiteStorageQuota,
          dataKey: OrderKeys.storageQuotaBytes,
          sortable: true,
          headerText: Messages.column_storageQuota(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(SiteGridCells.StorageQuotaFormatter)
        };
      case CommonColumn.SiteStatus:
        return {
          name: CommonColumn.SiteStatus,
          dataKey: OrderKeys.availability,
          sortable: true,
          headerText: Messages.column_status(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform('availability', TypeUnsafeTranslation.translate),
        };
      case CommonColumn.SiteMetricsEnabled:
        return {
          name: CommonColumn.SiteMetricsEnabled,
          dataKey: OrderKeys.metricsEnabled,
          sortable: true,
          headerText: Messages.column_metrics(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CenterAlignedCell(SiteGridCells.Checkmark('settings.metricsEnabled')),
        };
      case CommonColumn.SiteAuthoringEnabled:
        return {
          name: CommonColumn.SiteAuthoringEnabled,
          dataKey: OrderKeys.authoringEnabled,
          sortable: true,
          headerText: Messages.column_webAuthoring(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CenterAlignedCell(SiteGridCells.Checkmark('settings.authoringEnabled')),
        };
      case CommonColumn.SiteSheetImageEnabled:
        return {
          name: CommonColumn.SiteSheetImageEnabled,
          dataKey: OrderKeys.sheetImageEnabled,
          sortable: true,
          headerText: Messages.column_sheetImage(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CenterAlignedCell(SiteGridCells.Checkmark('siteAdminSettings.sheetImageEnabled')),
        };
      case CommonColumn.SiteEditMembership:
        return {
          name: CommonColumn.SiteEditMembership,
          dataKey: OrderKeys.siteAdminsCanAddUsers,
          sortable: true,
          headerText: Messages.column_editMembership(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CenterAlignedCell(SiteGridCells.Checkmark('settings.siteAdminsCanAddUsers')),
        };
      case CommonColumn.SiteExtractEncryption:
        return {
          name: CommonColumn.SiteExtractEncryption,
          dataKey: OrderKeys.extractEncryptionMode,
          sortable: true,
          headerText: Messages.column_extractEncryptionMode(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CenterAlignedCell(SiteGridCells.ExtractEncryptionModeCell()),
        };
      case CommonColumn.SiteAskDataMode:
        return {
          name: CommonColumn.SiteAskDataMode,
          dataKey: OrderKeys.askDataMode,
          sortable: false,
          headerText: Messages.column_askDataMode(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CenterAlignedCell(SiteGridCells.AskDataModeCellFormatter()),
        };
      case CommonColumn.SiteGuestAccessEnabled:
        return {
          name: CommonColumn.SiteGuestAccessEnabled,
          dataKey: OrderKeys.guestAccessEnabled,
          sortable: true,
          headerText: Messages.column_guestAccessEnabled(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: CenterAlignedCell(SiteGridCells.Checkmark('settings.guestAccessEnabled')),
        };
      case CommonColumn.ScheduleFrequency:
        return {
          name: CommonColumn.ScheduleFrequency,
          dataKey: OrderKeys.scheduleType,
          sortable: true,
          headerText: Messages.column_schedule_frequency(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: SchedulesGridCells.FrequencyCell()
        };
      case CommonColumn.ScheduleTaskType:
        return {
          name: CommonColumn.ScheduleTaskType,
          dataKey: OrderKeys.scheduledAction,
          sortable: true,
          headerText: Messages.column_schedule_type(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: SchedulesGridCells.TaskTypeCell()
        };
      case CommonColumn.ScheduleTasks:
        return {
          name: CommonColumn.ScheduleTasks,
          dataKey: OrderKeys.taskCount,
          sortable: true,
          headerText: Messages.column_schedule_task_count(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('taskCount'))
        };
      case CommonColumn.ScheduleLinkedTask:
        return {
          name: CommonColumn.ScheduleLinkedTask,
          dataKey: OrderKeys.linkedTask,
          sortable: false,
          headerText: Messages.column_schedule_linked_task(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkedTaskCell(),
        };
      case CommonColumn.ScheduleExecution:
        return {
          name: CommonColumn.ScheduleExecution,
          dataKey: OrderKeys.parallel,
          sortable: true,
          headerText: Messages.column_schedule_behavior(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: SchedulesGridCells.ExecutionCell()
        };
      case CommonColumn.ScheduleNextRunAt:
        return {
          name: CommonColumn.ScheduleNextRunAt,
          dataKey: OrderKeys.runNextAt,
          sortable: true,
          headerText: Messages.column_schedule_next_run(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DateTimeCell('runNextAt', Messages.disabled())
        };
        case CommonColumn.AccelerationUpdatedAt:
          return {
            name: CommonColumn.AccelerationUpdatedAt,
            dataKey: OrderKeys.accelerationUpdatedAt,
            sortable: true,
            headerText: Messages.dataAccelerationLastUpdateTime(),
            direction: 'asc',
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: DateTimeCell('updatedAt', Messages.disabled())
          };
      case CommonColumn.SiteName:
        return {
          name: CommonColumn.SiteName,
          dataKey: OrderKeys.siteName,
          sortable: true,
          headerText: Messages.column_task_site(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform('site',
            (site: ISiteNameWithId) => site.name)
        };
      case CommonColumn.ScheduleName:
        let scheduleNameColumn: DataGrid.Column = {
          name: CommonColumn.ScheduleName,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForScheduleName)
        };
        return scheduleNameColumn;
      case CommonColumn.GroupName:
        let groupNameColumn: DataGrid.Column = {
          name: CommonColumn.GroupName,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: GroupDataGridCells.ProfilePictureCell(
            (row: DataGrid.Row) => {
              const group = row.data as IGroupInfo;
              return {
                profileDisplayName: group.name,
                imageUrl: '',
                divShape: ProfilePicture.Shape.Square,
                isAllUsersGroup: group.isAllUsersGroup
              };
            },
            (row: DataGrid.Row) => {
              return this.generateLinkForGroupName(row);
            }
          )
        };
        return groupNameColumn;
      case CommonColumn.GroupDomain:
        return {
          name: CommonColumn.GroupDomain,
          dataKey: OrderKeys.domainName,
          sortable: true,
          headerText: Messages.column_domainName(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCell(OrderKeys.domainName)
        };
      case CommonColumn.GroupUserCount:
        return {
          name: CommonColumn.GroupUserCount,
          dataKey: OrderKeys.userCount,
          sortable: true,
          headerText: Messages.column_userCount(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('userCount'))
        };
      case CommonColumn.GroupMinSiteRole:
        return {
          name: CommonColumn.GroupMinSiteRole,
          dataKey: OrderKeys.minimumSiteRole,
          sortable: true,
          headerText: Messages.column_minimumSiteRole(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: GroupDataGridCells.MinimumSiteRoleCell()
        };
      case CommonColumn.GroupLicenseOnSignIn:
        return {
          name: CommonColumn.GroupLicenseOnSignIn,
          dataKey: OrderKeys.grantLicenseMode,
          sortable: true,
          headerText: Messages.column_license_on_signin(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: CenterAlignedCell(SiteGridCells.LicenseOnSignInFormatter),
        };
      case CommonColumn.GroupExternalUserEnabled:
        return {
          name: CommonColumn.GroupExternalUserEnabled,
          dataKey: OrderKeys.externalUserEnabled,
          sortable: true,
          headerText: Messages.column_ephermeralUsers(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: CenterAlignedCell(SiteGridCells.ExternalUserEnabledFormatter),
        };
      case CommonColumn.GroupSetName:
        let groupSetNameColumn: DataGrid.Column = {
          name: CommonColumn.GroupSetName,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: GroupDataGridCells.ProfilePictureCell(
            (row: DataGrid.Row) => {
              const group = row.data as IGroupSetInfo;
              return {
                profileDisplayName: group.name,
                imageUrl: '',
                divShape: ProfilePicture.Shape.Square,
              };
            },
            (row: DataGrid.Row) => {
              return this.generateLinkForGroupSetName(row);
            }
          )
        };
        return groupSetNameColumn;
      case CommonColumn.GroupSetGroupCount:
        return {
          name: CommonColumn.GroupSetGroupCount,
          dataKey: OrderKeys.groupCount,
          sortable: false, // GroupSet group counts are not sortable via API
          headerText: Messages.column_groupCount(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(PlainTextCell(OrderKeys.groupCount))
        };
      case CommonColumn.SiteUserName:
        return {
          name: CommonColumn.SiteUserName,
          dataKey: OrderKeys.username,
          sortable: true,
          headerText: Messages.column_username(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCell(OrderKeys.username)
        };
      case CommonColumn.SiteUserRole:
        return {
          name: CommonColumn.SiteUserRole,
          dataKey: OrderKeys.siteRole,
          sortable: true,
          headerText: Messages.column_site_role(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform(
            'siteRole',
            SiteRolesHelper.siteRoleToName
          )
        };
      case CommonColumn.SiteUserGroupCount:
        return {
          name: CommonColumn.SiteUserGroupCount,
          dataKey: OrderKeys.groupCount,
          sortable: true,
          headerText: Messages.column_groupCount(),
          direction: 'asc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('groupCount'))
        };
      case CommonColumn.SiteUserLastSignin:
        return {
          name: CommonColumn.SiteUserLastSignin,
          dataKey: OrderKeys.lastSignIn,
          sortable: true,
          headerText: Messages.column_last_sign_in(),
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DateTimeCell('lastSignIn')
        };
      case CommonColumn.SiteUserDomainName:
        return {
          name: CommonColumn.SiteUserDomainName,
          dataKey: OrderKeys.domainName,
          sortable: true,
          headerText: Messages.column_domainName(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCell(OrderKeys.domainName)
        };
      case CommonColumn.SiteUserAuthentication:
        return {
          name: CommonColumn.SiteUserAuthentication,
          dataKey: CommonColumn.SiteUserAuthentication,
          sortable: false,
          headerText: Messages.column_authentication(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: UserDataGridCells.AuthenticationCell
        };
      case CommonColumn.SiteUserDisplayName:
        return this.siteUserDataGridColumn(sortableHeaderFormatter);
      case CommonColumn.SearchUserOwnedViewsCount:
        return {
          name: CommonColumn.SearchUserOwnedViewsCount,
          dataKey: CommonColumn.SearchUserOwnedViewsCount,
          sortable: false,
          direction: 'asc',
          headerText: Messages.column_owned_views(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell((item: any) => item.ownedItemCounts.viewCount))
        };
      case CommonColumn.SearchUserOwnedWorkbooksCount:
        return {
          name: CommonColumn.SearchUserOwnedWorkbooksCount,
          dataKey: CommonColumn.SearchUserOwnedWorkbooksCount,
          sortable: false,
          direction: 'asc',
          headerText: Messages.column_owned_workbooks(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell((item: any) => item.ownedItemCounts.workbookCount))
        };
      case CommonColumn.SearchUserOwnedDatasourcesCount:
        return {
          name: CommonColumn.SearchUserOwnedDatasourcesCount,
          dataKey: CommonColumn.SearchUserOwnedDatasourcesCount,
          sortable: false,
          direction: 'asc',
          headerText: Messages.column_owned_datasources(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell((item: any) => item.ownedItemCounts.datasourceCount))
        };
      case CommonColumn.ServerUserDisplayName:
        return {
          name: CommonColumn.ServerUserDisplayName,
          dataKey: OrderKeys.displayName,
          sortable: true,
          direction: 'asc',
          growToFit: true,
          headerText: Messages.column_fullName(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: UserDataGridCells.ProfilePictureCell(
            (row: DataGrid.Row) => {
              const user = row.data as IServerUserInfo;
              return {
                profileDisplayName: user.displayName,
                imageUrl: user.userImageUrl,
                divShape: ProfilePicture.Shape.Circle
              };
            },
            (row: DataGrid.Row) => {
              const user = row.data as IServerUserInfo;
              const disabled = user.readOnly || user.maxSiteRole === SiteRoles.Guest;
              return {
                text: user.displayName,
                url: disabled ? null : ServerUrls.getInstance().userSettingsUrl(user.domainName, user.username),
                style: disabled ? { color: CommonStyles.Colors.Grey102, textDecoration: 'none', pointerEvents: 'none' } : null
              };
            }
          )
        };
      case CommonColumn.ServerUserMaxSiteRole:
        return {
          name: CommonColumn.ServerUserMaxSiteRole,
          dataKey: OrderKeys.maxSiteRole,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_max_site_role(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform(
            'maxSiteRole',
            SiteRolesHelper.siteRoleToName
          )
        };
      case CommonColumn.ServerUserLicenseLevel:
        return {
          name: CommonColumn.ServerUserLicenseLevel,
          dataKey: OrderKeys.licenseLevel,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_license_level(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform(
            'subscriptionLicensingRole',
            this.licenseLevelTransform(TypeUnsafeTranslation.translate)
          )
        };
      case CommonColumn.ServerUserSiteCounts:
        return {
          name: CommonColumn.ServerUserSiteCounts,
          dataKey: OrderKeys.siteCount,
          sortable: true,
          direction: 'asc',
          headerText: Messages.column_site_count(),
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('siteCount'))
        };
        case CommonColumn.SiteUserIdentityPool:
          return {
            name: CommonColumn.SiteUserIdentityPool,
            dataKey: CommonColumn.SiteUserIdentityPool,
            sortable: false,
            headerText: Messages.column_identityPool(),
            headerFormatter: SortableHeader.Formatter(this.params.currentSort, this.params.changeSort,
              <QuickTooltip testId={SpaceHeaderTestIds.name} content={Messages.identityPools_HeaderTooltip()}/>),
            cellFormatter: UserDataGridCells.IdentityPoolsCell()
        };
      case CommonColumn.CollectionItemCount:
        return {
          name: CommonColumn.CollectionItemCount,
          dataKey: OrderKeys.permissionedItemCount,
          headerText: Messages.collectionItemCountColumn(),
          sortable: true,
          direction: 'desc',
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(LocalizedCountCell('permissionedItemCount')),
        };

      case CommonColumn.CollectionName:
        return {
          name: CommonColumn.CollectionName,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkWithCollectionVisibility(this.generateLinkForName)
        };

      case CommonColumn.CollectionVisibility:  // TODO: remove column with CollectionsPermissions flag
        return {
          name: CommonColumn.CollectionVisibility,
          dataKey: OrderKeys.visibility,
          headerText: Messages.collectionVisibilityColumn(),
          sortable: false,
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform(
            'visibility',
            (visibility: CollectionVisibility) => visibility === 'private'
              ? Messages.collectionVisibilityPrivateCellLabel()
              : Messages.collectionVisibilityPublicCellLabel(),
          )
        };

      case CommonColumn.SharedBy:
        return {
          name: CommonColumn.SharedBy,
          dataKey: OrderKeys.sharedBy,
          headerText: Messages.sharedBy(),
          sortable: false,
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: UserDataGridCells.ProfilePictureCell(
            (row: DataGrid.Row) => {
              const share = row.data as ISharedWithMeContentInfo;
              return {
                profileDisplayName: share.sharer.displayName,
                imageUrl: share.sharer.isDeleted ? null : share.sharer.userImageUrl,
                divShape: ProfilePicture.Shape.Circle,
              };
            },
            (row: DataGrid.Row) => {
              const share = row.data as ISharedWithMeContentInfo;
              const disabled = share.sharer.isDeleted; // TODO: update this when API signature is confirmed
              return {
                text: disabled ? Messages.sharedWithMeRemovedUser({ USERNAME: share.sharer.username }) : share.sharer.displayName,
                url: disabled ? null : this.params.urls.userUrl(share.sharer.username, share.sharer.domainName),
                style: disabled ? { color: CommonStyles.Colors.Grey102, textDecoration: 'none', pointerEvents: 'none' } : null,
              };
            }
          )
        };

      case CommonColumn.SharedCustomizedView:
        return {
          name: CommonColumn.SharedCustomizedView,
          dataKey: OrderKeys.sharedCustomizedView,
          headerText: Messages.sharedWithMeSharedVersionsTitle(),
          resizable: false,
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: SharedViewsCell(
            (row: DataGrid.Row) => {
              const share = row.data as ISharedWithMeContentInfo;
              return {
                showButton: share.isShareActivityIconVisible && !share.isAccessDenied,
                contentId: share.id,
              };
            },
            this.params.urls,
          ),
        };

      case CommonColumn.SharedAt:
        return {
          name: CommonColumn.SharedAt,
          dataKey: OrderKeys.sharedAt,
          headerText: Messages.sharedAt(),
          sortable: false,
          direction: 'desc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DateTimeCell(OrderKeys.sharedAt),
        };

      case CommonColumn.SharedWith:
        return {
          name: CommonColumn.SharedWith,
          dataKey: OrderKeys.sharedWith,
          headerText: Messages.sharedWith(),
          sortable: true,
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCell(OrderKeys.sharedWith),
        };

      case CommonColumn.ConnectedApplicationStatus:
        return {
          name: CommonColumn.ConnectedApplicationStatus,
          dataKey: 'enabled',
          headerText: Messages.connectedApplicationsStatus(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform('enabled', (enabled: boolean) => enabled ? Messages.connectedApplicationsEnabled() : Messages.connectedApplicationsDisabled()),
        };

      case CommonColumn.ConnectedApplicationClientId:
        return {
          name: CommonColumn.ConnectedApplicationClientId,
          dataKey: 'id',
          headerText: Messages.connectedApplicationsClientId(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCell('id'),
        };

      case CommonColumn.ConnectedApplicationTrustType:
        return {
          name: CommonColumn.ConnectedApplicationTrustType,
          dataKey: 'trustType',
          headerText: Messages.connectedApplicationsTrustType(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCell('trustType'),
        };

      case CommonColumn.ConnectedApplicationAuthorizationServer:
        return {
          name: CommonColumn.ConnectedApplicationAuthorizationServer,
          dataKey: 'authorizationServerType',
          headerText: Messages.connectedApplicationsAuthorizationServer(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCell('authorizationServerType'),
        };

      case CommonColumn.DataAccelerationStatus:
        return {
          name: CommonColumn.DataAccelerationStatus,
          dataKey: 'accelerationStatus',
          headerText: Messages.accelerationStatus(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransformAndTooltip('accelerationStatus', getAdminAccelerationStatusForTaskPage,
            getAdminAccelerationStatusTooltipContentForTaskPage)
        };
      case CommonColumn.Acceleration:
        return showAccelerationStatusInViewSubspace() ? {
          name: CommonColumn.Acceleration,
          dataKey: 'acceleration',
          headerText: Messages.column_acceleration(),
          sortable: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransformAndTooltip('acceleration', getAccelerationStatusForViewSpace,
            getAccelerationStatusTooltipContentForViewSpace),
        } : undefined;
      case CommonColumn.MonitorDataChanges:
        return showMonitorDataChangesInViewSubspace() ? {
          name: CommonColumn.MonitorDataChanges,
          dataKey: OrderKeys.monitorDataChangesStatus,
          sortable: false,
          headerText: Messages.column_monitor_data_changes(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransformAndTooltip(
            OrderKeys.monitorDataChangesStatus,
            getMonitorDataChangesStatusForViewSpace,
            getMonitorDataChangesStatusTooltipContentForViewSpace
          )
        } : undefined;
      case CommonColumn.PublishedConnectionPolicies:
        return {
          name: CommonColumn.PublishedConnectionPolicies,
          dataKey: OrderKeys.hasDataPolicy,
          sortable: true,
          direction: 'asc',
          preferredWidth: 170,
          headerText: Messages.column_policies(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithIcon((row: any) => {
            const rowData = row as IPublishedConnectionTableBase;
            return {
              text: rowData.hasDataPolicy ? Messages.policiesApplied() : '',
              iconClass: rowData.hasDataPolicy ? 'tb-published-connection-policies-applied' : '',
            };
          })
        };

      case CommonColumn.PublishedConnectionTableName:
        return {
          name: CommonColumn.PublishedConnectionTableName,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.column_name(),
          direction: 'asc',
          growToFit: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForName, true, this.params.urls)
        };

      case CommonColumn.PublishedConnectionExtractStatus: {
        return {
          name: CommonColumn.ExtractStatus,
          dataKey: OrderKeys.extractRefreshedTime,
          sortable: true,
          headerText: Messages.extractStatusLabel(),
          direction: 'asc',
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform('lastRefreshedAt', this.publishedConnectionExtractStatus),
        };
      }

      case CommonColumn.PublishedConnectionScheduleType:{
        return {
          name: CommonColumn.PublishedConnectionRunHistoryTable,
          dataKey: OrderKeys.hasExtracts,
          sortable: true,
          headerText: Messages.linkedTasksScheduleTypeColumnHeader(),
          direction: 'asc',
          growToFit: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForExtractScheduleType)
        };
      }

      case CommonColumn.PublishedConnectionRunHistoryTable:{
        return {
          name: CommonColumn.PublishedConnectionRunHistoryTable,
          dataKey: OrderKeys.hasExtracts,
          sortable: true,
          headerText: Messages.table(),
          direction: 'asc',
          growToFit: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(this.generateLinkForExtractTaskName)
        };
      }

      case CommonColumn.PublishedConnectionTableSync:{
        return {
          name: CommonColumn.PublishedConnectionTableSync,
          dataKey: OrderKeys.refreshType,
          sortable: true,
          headerText: Messages.tableSync(),
          direction: 'asc',
          growToFit: false,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform('extractServiceTask', this.publishedConnectionTableSync)
        };
      }

      case CommonColumn.DataType:
        return {
          name: CommonColumn.DataType,
          dataKey: OrderKeys.dataType,
          headerText: Messages.column_content_type(),
          sortable: true,
          headerFormatter: sortableHeaderFormatter,
          resizable: false,
          cellFormatter: ContentTypeCell({
            iconTypeForRow: this.dataTypeIconForRow,
            titleForIcon: this.dataTypeTitleForIconForRow,
            isCertified: () => false,
            getTooltipProps: () => null,
          })
        };
      case CommonColumn.ResourceGovernanceSite:
        return {
          name: CommonColumn.ResourceGovernanceSite,
          dataKey: OrderKeys.name,
          sortable: true,
          headerText: Messages.siteName(),
          direction: 'asc',
          growToFit: true,
          preferredWidth: 9001,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCell('siteName'),
        };
      case CommonColumn.ResourceGovernanceConfiguration:
        return {
          name: CommonColumn.ResourceGovernanceConfiguration,
          dataKey: OrderKeys.isDefault,
          sortable: false,
          headerText: Messages.resourceGovernanceColumnConfiguration(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform(OrderKeys.isDefault, isDefault => isDefault
            ? Messages.resourceGovernanceDefault()
            : Messages.resourceGovernanceCustom()),
        };
      case CommonColumn.ResourceGovernanceExtracts:
        return {
          name: CommonColumn.ResourceGovernanceExtracts,
          dataKey: OrderKeys.extractsConcurrencyLimit,
          sortable: false,
          headerText: Messages.resourceGovernanceColumnExtracts(),
          direction: 'asc',
          growToFit: true,
          preferredWidth: 80,
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(ResourceGovernanceGridCells.resourceGovernanceLimitFormatter(OrderKeys.extractsConcurrencyLimit)),
        };
      case CommonColumn.ResourceGovernanceSubscriptions:
        return {
          name: CommonColumn.ResourceGovernanceSubscriptions,
          dataKey: OrderKeys.subscriptionsConcurrencyLimit,
          sortable: false,
          headerText: Messages.resourceGovernanceColumnSubscriptions(),
          direction: 'asc',
          growToFit: true,
          preferredWidth: 105,
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(ResourceGovernanceGridCells.resourceGovernanceLimitFormatter(OrderKeys.subscriptionsConcurrencyLimit)),
        };
      case CommonColumn.ResourceGovernanceFlows:
        return {
          name: CommonColumn.ResourceGovernanceFlows,
          dataKey: OrderKeys.flowsConcurrencyLimit,
          sortable: false,
          headerText: Messages.resourceGovernanceColumnFlows(),
          direction: 'asc',
          growToFit: true,
          preferredWidth: 80,
          headerFormatter: RightAlignedHeader(sortableHeaderFormatter),
          cellFormatter: RightAlignedCell(ResourceGovernanceGridCells.resourceGovernanceLimitFormatter(OrderKeys.flowsConcurrencyLimit)),
        };
      case CommonColumn.ResourceGovernanceRuntimeLimits:
        return {
          name: CommonColumn.ResourceGovernanceRuntimeLimits,
          dataKey: OrderKeys.runtimeLimit,
          sortable: false,
          headerText: Messages.resourceGovernanceColumnRuntimeLimits(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform(OrderKeys.runtimeLimit, limit => limit === undefined
            ? Messages.resourceGovernanceNoLimitValue()
            : Messages.resourceGovernanceColumnRuntimeHours({ RUNTIME: L10n.localizedCount(limit) })),
        };
      case CommonColumn.ResourceGovernanceActualRuntime:
        return {
          name: CommonColumn.ResourceGovernanceActualRuntime,
          dataKey: OrderKeys.actualRunTime,
          sortable: false,
          headerText: Messages.resourceGovernanceColumnActualRuntime(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: PlainTextCellWithTransform(OrderKeys.actualRunTime, runtime => runtime === undefined
            ? Messages.resourceGovernanceColumnRuntimeHours({ RUNTIME: L10n.localizedFloat(0) })
            // Runtime is in seconds so we divide by 3600 to turn it into hours. Localized float ensures the appropriate precision in the displayed result
            : Messages.resourceGovernanceColumnRuntimeHours({ RUNTIME: L10n.localizedFloat(runtime / 3600) })),
        };
      case CommonColumn.ResourceGovernanceJobs:
        return {
          name: CommonColumn.ResourceGovernanceJobs,
          dataKey: OrderKeys.siteId,
          sortable: false,
          headerText: Messages.resourceGovernanceColumnJobs(),
          direction: 'asc',
          growToFit: true,
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: LinkCell(row => {
            const urls = new SpacesSiteUrls({ siteUrlName: row.data.siteUrlName });
            const link: ILink = {
              text: Messages.resourceGovernanceJobsPage({ SITE_NAME: row.data.siteName }),
              url: urls.jobsUrl(),
            };
            return link;
          }),
        };
      case CommonColumn.DatabaseLocation:
        return {
          name: CommonColumn.DatabaseLocation,
          dataKey: OrderKeys.containerName,
          sortable: true,
          headerText: Messages.column_location(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataConnectionDataGridCells.UsernameCellFormatter()
        };
      case CommonColumn.TableLocation:
        return {
          name: CommonColumn.TableLocation,
          dataKey: OrderKeys.containerName,
          sortable: true,
          headerText: Messages.column_location(),
          headerFormatter: sortableHeaderFormatter,
          cellFormatter: DataConnectionDataGridCells.UsernameCellFormatter()
        };
        case CommonColumn.TableSource:
          return {
            name: CommonColumn.TableSource,
            dataKey: OrderKeys.tableDatabase,
            sortable: false,
            headerText: Messages.column_table_source(),
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: PlainTextCellWithNestedValue('database','name')
          };
      default:
        throw new Error('Column not yet implemented: ' + columnDefinition);
    }
  }

  private getOrderKeysBasedOnContentType() {
    return ((isSiteSpace(this.params.spaceInfo) && this.params.spaceInfo.subspaceName === 'view' && FeatureFlags.LocationNameSortEnabled) ? OrderKeys.locationName : OrderKeys.containerName);
  }

  private getLastRunAtExtractRefreshes(sortableHeaderFormatter: React.Factory<any>): DataGrid.Column {
    const lastUpdateCell = InteractiveIconTooltipCell((row: DataGrid.Row) => {
      const extractTaskInfo = row.data as IExtractTaskInfo;
      const dateTime = extractTaskInfo.lastRunAt;
      const dateTimeText = dateTime ? Formatters.formatMediumDateTimeWithoutSeconds(dateTime) : Messages.never();
      if (extractTaskInfo.state === 'Disabled') {
        const message = dateTime ? Messages.subscriptionSuspended({ SUSPENDED_DATE: dateTimeText }) : Messages.suspended();
        return {
          text: message,
          title: Messages.extractRefreshTooltipTitle(),
          iconClass: 'tb-react-dg-hcol tb-icon-server-alert-list-suspended',
          tooltipText: Messages.extractRefreshPausedMessage(),
          tooltipLinkText: Messages.learnMore(),
          tooltipLink: HelpService.getServerHelpUrlByCategory('extract-auto-suspend')
        };
      } else {
        return {
          text: dateTimeText,
          title: Messages.extractRefreshTooltipTitle(),
        };
      }
    });

    return {
      name: CommonColumn.LastRunAtExtractRefreshes,
      dataKey: OrderKeys.lastRunAt,
      headerText: Messages.column_task_last_update(),
      direction: 'asc',
      headerFormatter: sortableHeaderFormatter,
      cellFormatter: lastUpdateCell,
      sortable: true,
    };
  }

  private buildProbablyOrEager(rowData: Dictionary<any>, category: 'warning' | 'certification'){
    const isCertificationActive = getCertificationActivityFromRowData(rowData);
    const severity = getWarningSeverityFromRowData(rowData);
    const isWarningActive = severity !== undefined;
    switch (category) {
      case 'certification':
        return isCertificationActive ? {
          certified: isCertificationActive
        }: undefined;
      case 'warning':
        return isWarningActive ? {
          warning: {
            value: 'Warning',
            severe: severity,
            count: 1,
          },
        } : undefined;
    }
  }

  private createLabelGridColumnFormatter = (category: 'warning' | 'certification'): DataGrid.Column => {
    const listViewGridColumnGenerator = this;

    function getPropsFromRowData(rowData: Dictionary<any>): UseRenderLabelsParams {
      return {
        catalogEnabled: listViewGridColumnGenerator.server.catalogingOnSiteEnabled(),
        content: {
          contentId: rowData.id,
          contentLuid: rowData.luid,
          contentType: rowData.contentType,
          contentName: rowData.name ?? undefined,
          contentUrl: urlForContentType(listViewGridColumnGenerator.siteUrls, rowData as IContentInfo),
          subjectArea: rowData.contentType,
        },
        probablyOrEager: listViewGridColumnGenerator.buildProbablyOrEager(rowData, category),
        testId: rowData.contentType + '-' + category + '-column',
        controls: {
          kind: 'icon',
          siteUrls: listViewGridColumnGenerator.siteUrls,
        }
      };
    }

    const col = createLabelColumnFormatter(category, { overrides: {}, getPropsFromRowData }) as DataGrid.Column;
    return col;
  };

  private licenseLevelTransform(translate: IReactTranslateService): (licenseRole: string) => string {
    return (licenseRole: string) => licenseRole ? translate('license_' + licenseRole) : '';
  }

  private isAccessDenied = (row: DataGrid.Row): boolean => {
    return !!row.data.isAccessDenied;
  };

  private isRowDisabled = (row: DataGrid.Row): boolean => {
    return row.data.id === '';
  };

  private isEmbeddedDatasource = (row: DataGrid.Row): boolean => {
    return row.data.contentType === 'datasource' && !!row.data.workbook;
  };

  private isDatabaseContentType = (row: DataGrid.Row): boolean => {
    return row.data.contentType === 'database';
  };

  private isTableContentType = (row: DataGrid.Row): boolean => {
    return row.data.contentType === 'table';
  };

  private isNeverPublishedFlow = (row: DataGrid.Row): boolean => {
    return row.data.contentType === 'flow' && row.data.draftStatus === ContentDraftStatus.unpublished;
  };

  private isNeverPublishedVirtualConnection = (row: DataGrid.Row): boolean => {
    return row.data.contentType === 'virtualConnection' && row.data.draftStatus === ContentDraftStatus.unpublished;
  };

  private isUnpublishedWorkbook = (row: DataGrid.Row): boolean => {
    return row.data.contentType === 'workbook' && row.data.draftStatus === ContentDraftStatus.unpublished;
  };

  private isLensContentType = (row: DataGrid.Row): boolean => {
    return row.data.contentType === 'lens';
  };

  private isFavoritableLens(row: DataGrid.Row): boolean {
    return !FeatureFlags.askDataLensFavoritingEnabled && this.isLensContentType(row);
  }

  private isUnifiedDataSource = (row: DataGrid.Row): boolean => {
    return DataSources.isUnifiedDatasource(row.data as IContentInfo);
  };

  private isCustomView = (row: DataGrid.Row): boolean => {
    return row.data.contentType === 'customView';
  };

  private isMetric = (row: DataGrid.Row): boolean => {
    return row.data.contentType === 'metric';
  };

  private isConnectedApp = (row: DataGrid.Row): boolean => {
    return row.data.contentType === 'connectedApplication';
  };

  private isCertifiedDatasource = (row: DataGrid.Row): boolean => {
    if (row.data.contentType !== 'datasource') {
      return false;
    }
    return DataSources.isCertified(getCommonDatasourceInfo(row.data as IContentInfo | IDataSourceInfo));
  };

  private isFavoritable = (row: DataGrid.Row): boolean => {
    return !this.isEmbeddedDatasource(row) &&
      !this.isNeverPublishedFlow(row) &&
      !this.isUnpublishedWorkbook(row) &&
      !this.isNeverPublishedVirtualConnection(row) &&
      !this.isFavoritableLens(row) &&
      !this.isDatabaseContentType(row) &&
      !this.isTableContentType(row);
  };

  private isUnpublishedContent = (row: DataGrid.Row): boolean => {
    return this.isNeverPublishedFlow(row) || this.isUnpublishedWorkbook(row) || this.isNeverPublishedVirtualConnection(row);
  };

  /* If the data is a unified data source, the id will be the connection id which does not work for favoriting */
  private getItemForFavoriting(row: DataGrid.Row): IFavoritableItemInfo {
    // If the data is a unified data source, the id will be the connection id which does not work for favoriting
    const idForFavoriting = this.isUnifiedDataSource(row) ? (row.data as IDataSourceInfo).idForFavoriting : row.data.id;
    return {
      id: row.data.id,
      contentType: row.data.contentType,
      name: row.data.name,
      idForFavoriting,
    };
  }

  private onAddFavorite = (row: DataGrid.Row): void => {
    this.params.addContentFavorite(this.getItemForFavoriting(row));
  };

  private onRemoveFavorite = (row: DataGrid.Row): void => {
    this.params.removeContentFavorite(this.getItemForFavoriting(row));
  };

  private isRowSelected = (row: DataGrid.Row): boolean => {
    return !!this.params.selectedItems[getKeyForExplorableItem(row.data as IExplorableItemTypeInfo)];
  };

  private onSelectedCheckboxClicked = (row: DataGrid.Row): void => {
    const item = row.data as IContentInfo;

    if (this.isRowSelected(row)) {
      this.params.deselectItem(item);
    } else {
      this.params.selectItem(item);
    }
  };

  private checkboxCellTestId = (row: DataGrid.Row): string => {
    return 'select-' + row.key;
  };

  private favoriteCellTestId = (row: DataGrid.Row): string => {
    return 'favorite-' + row.key;
  };

  private dataConnectionDataSourceCellTestId = (row: DataGrid.Row): string => {
    return 'dataConnectionDataSource-' + row.key;
  };

  private contentTypeIconForRow = (row: DataGrid.Row): (props: any) => React.ReactSVGElement | null => {
    if (row.data.contentType === 'datasource') {
      return DataSources.getIconForDatasourceType(getCommonDatasourceInfo(row.data as IContentInfo | IDataSourceInfo), 'list');
    } else if (row.data.contentType === 'virtualConnection') {
      return PublishedConnections.getPublishedConnectionIcon(row.data as ISpaceItemDetailInfo<'virtualConnection'>, false);
    } else if (row.data.contentType === 'virtualConnectionTable') {
      return getPublishedConnectionTableIcon(row.data as ISpaceItemDetailInfo<'virtualConnectionTable'>, false);
    } else if (row.data.contentType === 'database') {
      return Databases.getIconForDatabaseType(row.data as AnyDatabaseType);
    } else if (row.data.contentType === 'table') {
      return Tables.getIconForTableType();
    }

    const explorableItem: ExplorableItemDefinition = getExplorableItemDefinition(row.data.contentType as ExplorableItemType);

    return explorableItem.listViewIconType;
  };

  private contentTypeTitleForIconForRow = (row: DataGrid.Row): string => {
    const itemDefinition = getExplorableItemDefinition(row.data.contentType as ExplorableItemType);
    // datasource case return more information for icon title
    if (row.data.contentType === 'datasource') {
      if (row.data.workbook) {
        return Messages.column_datasource_icon_title_emb_ds({ LIVE_OR_EXTRACT: row.data.hasExtracts ? Messages.extract() : Messages.live(), UNICODE: String.fromCharCode(8212) });
        // Messages.datasource() + ' (' +(row.data.hasExtracts? 'Extract':'Live')+ '-embedded in workbook)';
      }
      return Messages.datasource();
    } else if (itemDefinition && itemDefinition.hasOwnProperty('singularName')) {
      return (itemDefinition as any).singularName();
    }

    return '';
  };

  private contentTypeForRow = (row: DataGrid.Row): ExplorableItemType => {
    return row.data.contentType as ExplorableItemType;
  };

  private dataTypeIconForRow = (row: DataGrid.Row): (props: any) => React.ReactSVGElement | null => {
    if (!row.data.dataType) {
      return null;
    }

    switch (row.data.dataType) {
      case TableauDataType.Boolean:
        return BooleanBaseIcon;
      case TableauDataType.Date:
        return CalendarBaseIcon;
      case TableauDataType.DateTime:
        return CalendarClockIcon;
      case TableauDataType.Integer:
      case TableauDataType.Real:
        return NumberBaseIcon;
      case TableauDataType.Spatial:
        return GlobeBaseIcon;
      case TableauDataType.String:
        return TextStringIcon;
    }

    return null;
  };

  private dataTypeTitleForIconForRow = (row: DataGrid.Row): string => {
    if (!row.data.dataType) {
      return '';
    }

    switch (row.data.dataType) {
      case TableauDataType.Boolean:
        return Messages.dataType_boolean();
      case TableauDataType.Date:
        return Messages.dataType_date();
      case TableauDataType.DateTime:
        return Messages.dataType_dateTime();
      case TableauDataType.Integer:
        return Messages.dataType_integer();
      case TableauDataType.Real:
        return Messages.dataType_real();
      case TableauDataType.Spatial:
        return Messages.dataType_spatial();
      case TableauDataType.String:
        return Messages.dataType_string();
    }

    return '';
  };

  private getContentUrl = (row: DataGrid.Row): string => {

    if (isSharedWithMeSpace(this.params.spaceInfo)) {
      // Check if there is just a single shared customView
      const customViewUrl = (row.data as ISharedWithMeContentInfo).customView?.url;
      if (customViewUrl) {
        return customViewUrl;
      }
    }

    if (this.isEmbeddedDatasource(row)) {
      return this.params.urls.workbookDatasourcesUrl(row.data.workbook.id);
    }

    if (this.isUnifiedDataSource(row)) {
      return this.params.urls.datasourceUrl(row.data.id);
    }

    if (this.isDatabaseContentType(row)) {
      const contentId = getGraphQLId(row.data as AnyDatabaseType);
      return this.params.urls.catalogDatabaseUrl(contentId);
    }

    if (this.isTableContentType(row)) {
      const contentId = getTableGraphQlId(row.data as AnyTableType);
      return this.params.urls.catalogTableUrl(contentId);
    }

    if (this.isCustomView(row)) {
      return this.params.urls.redirectToCustomViewUrlWithLuid(row.data.viewId, row.data.luid, row.data.urlId);
    }

    if (this.isConnectedApp(row)) {
      let connectedApplicationType = row.data.connectedApplicationType;
      if (connectedApplicationType === ConnectedApplicationType.EAS) {
        return this.params.urls.connectedApplicationEasUrl(row.data.id);
      }
      else if (connectedApplicationType === ConnectedApplicationType.DIRECT_TRUST) {
        return this.params.urls.connectedApplicationDirectTrustUrl(row.data.id);
      }
    }

    const contentType = row.data.contentType;
    // it only makes sense to generate a url for items that have their own space
    // and hence are SpaceInfoItemType('s)
    if (isSpaceInfoItemType(contentType)) {
      let temp = getContentSpaceDefinition(contentType).contentSpaceUrl(this.params.urls, row.key, row.data.luid);
      return temp;
    }
  };

  private getContentUrlForDraft = (row: DataGrid.Row): string => {
    if (this.isNeverPublishedFlow(row)) {
      return this.params.urls.flowEditorDraftUrl(row.data.draftLuid);
    } else if (this.isNeverPublishedVirtualConnection(row)) {
      const publishedConnectionEditorPrefix = '/published-connection-editor/';
      return `${publishedConnectionEditorPrefix}?draft=${row.data.draftLuid}`;
    }

    const result = extractWorkbookDraftEditUrlInfo(row.data as IContentInfo);
    return result.isUnpublishedWorkbookDraft ? result.editUrl : this.getContentUrl(row);
  };

  private generateLinkForProject = (row: DataGrid.Row): ILink => {

    if (row.data.workbook && row.data.workbook.project && row.data.contentType === 'view') {
      return {
        text: row.data.workbook.project.name,
        url: this.params.urls.projectUrl(row.data.workbook.project.id)
      };
    }

    if (row.data.project) {
      return {
        text: row.data.project.name,
        url: this.params.urls.projectUrl(row.data.project.id)
      };
    }

    return { text: '', url: '' };
  };

  private generateLinkForLocation = (row: DataGrid.Row): ILink => {

    const userId = getSessionManagementServiceInstance().sessionUserId();
    if (row.data.workbook && row.data.workbook.location && row.data.contentType === 'view') {
      let isOwner: boolean = row.data.workbook.owner && (userId === row.data.workbook.owner.id);
      return this.getLinkForLocationType(row.data.workbook.location, isOwner);
    } else if (row.data.workbook && row.data.workbook.project && row.data.contentType === 'view') {
      return {
        text: row.data.workbook.project.name,
        url: this.params.urls.projectUrl(row.data.workbook.project.id)
      };
    }

    const location = row.data.location;
    const isOwner: boolean = row.data.owner && (userId === row.data.owner.id);
    if (location) {
      return this.getLinkForLocationType(location, isOwner);
    } else if (row.data.project) {
      return {
        text: row.data.project.name,
        url: this.params.urls.projectUrl(row.data.project.id)
      };
    }

    return { text: '', url: '' };
  };

  private getLinkForLocationType = (location: ILocationInfo, isOwner: boolean): ILink => {
    switch (location.type) {
      case LocationTypes.PersonalSpace:
        return {
          text: Messages.personalSpaceGridLabel(),
          url: isOwner ? this.params.urls.personalSpaceUrl() : undefined,
          displayJustText: !isOwner
        };
      case LocationTypes.Project:
        return {
          text: location.name,
          url: this.params.urls.projectUrl(location.id)
        };
      default:
        return {
          text: '',
          url: ''
        };
    }
  };

  private generateLinkForDataconnectionDatasource = (row: DataGrid.Row): ILink => {

    if (!row.data.datasource) {
      // TODO check to see if this is a flow data connection for now return an empty link
      return { url: '', text: '' };
    }

    // we want to display the datasource information here, so grab the data source
    // Throw together object that adheres to DataGrid.Row interface and generate link for this ds
    const modifiedDatasourceRow = { key: row.data.datasource.id, data: DataSources.getDatasourceInfoFromDataConnection(row.data as IDataConnectionInfo) } as DataGrid.Row;
    return this.generateLinkForName(modifiedDatasourceRow, this.dataConnectionDataSourceCellTestId(row));
  };

  // Following logic is borrowed from app/content/ProjectGrid.ts
  private generateLinkForName = (row: DataGrid.Row, testId?: string): ILink => {
    const name = row.data.name;
    const description = TextileHelper.getPlainTextFromTextile(row.data.description, sanitize);
    const title = description ? name + '\n' + description : name;

    return {
      text: name,
      column: CommonColumn.Name,
      title: this.generateTitleForName(row, title),
      url: this.getContentUrl(row),
      displayJustText: isWorkbookSpace(this.params.spaceInfo) && this.isEmbeddedDatasource(row),
      testId: testId ? testId : 'name-' + row.key,
      isAccessDenied: this.isAccessDenied(row),
    };
  };

  // will only render status for flows or workbooks which support drafts, otherwise will just get the correct url based on the content type
  private generateLinkForNameWithDraft = (row: DataGrid.Row, testId?: string): ILinkWithDraftStatus => {
    const name = row.data.name;
    const description = TextileHelper.getPlainTextFromTextile(row.data.description, sanitize);
    const title = description ? name + '\n' + description : name;
    const sessionManagementService = getSessionManagementServiceInstance();
    const userId = sessionManagementService.sessionUserId();
    const isOwner: boolean = row.data.owner && (userId === row.data.owner.id);
    const displayJustText: boolean = (isWorkbookSpace(this.params.spaceInfo) && this.isEmbeddedDatasource(row)) ||
                                     (this.isNeverPublishedFlow(row) && !isOwner) ||
                                     (this.isUnpublishedWorkbook(row) && isPhoneSizedScreenOrUserProbablyOnMobile()) ||
                                     (this.isNeverPublishedVirtualConnection(row) && !isOwner);

    return {
      text: name,
      column: CommonColumn.Name,
      title: this.generateTitleForName(row, title),
      url: this.getContentUrlForDraft(row),
      displayJustText: displayJustText,
      testId: testId ? testId : 'name-' + row.key,
      draftStatus: row.data.draftStatus,
      openLinkInNewtab: this.isUnpublishedContent(row),
    };
  };

  private generateLinkForTasksFlowName = (row: DataGrid.Row, testId?: string): ILink => {
    const urls = this.params.urls.forSiteName(row.data.site.urlName);
    if (row.data.flow) {
      return {
        text: row.data.flow.name,
        url: urls.flowUrl(row.data.flow.id),
        iconClassName: 'tb-grid-icon-flow',
        iconTitle: Messages.flow()
      };
    }
  };

  private targetContentTypeIconForRow = (row: DataGrid.Row): (props: any) => React.ReactSVGElement | null => {
    if (row.data.datasource) {
      return DataSources.getIconforPublished(false, 'list');
    } else if (row.data.workbook) {
      return getExplorableItemDefinition('workbook' as ExplorableItemType).listViewIconType;
    } else if (row.data.virtualConnection) {
      return PublishedConnections.getPublishedConnectionIcon(row.data as ISpaceItemDetailInfo<'virtualConnection'>, false);
    }
    return null;
  };

  private targetContentTypeTitleForRow = (row: DataGrid.Row): string => {
    if (row.data.datasource) {
      return Messages.datasource();
    } else if (row.data.workbook) {
      return Messages.workbook();
    } else if (row.data.virtualConnection) {
      return Messages.publishedConnection();
    }
    return '';
  };

  private generateLinkForExtractTaskName = (row: DataGrid.Row, testId?: string): ILink => {
    try {
      const urls = this.params.urls.forSiteName(row.data.site.urlName);
      if (row.data.datasource) {
        const datasource: IDataSourceInfo = row.data.datasource;
        return {
          text: datasource.name,
          title: datasource.name,
          column: CommonColumn.ExtractTaskName,
          url: urls.datasourceUrl(datasource.id),
          testId: testId ? testId : 'name-' + row.key,
        };
      } else if (row.data.workbook) {
        const workbook: IWorkbookInfo = row.data.workbook;
        return {
          text: workbook.name,
          title: workbook.name,
          column: CommonColumn.ExtractTaskName,
          url: urls.workbookUrl(workbook.id),
          testId: testId ? testId : 'name-' + row.key,
        };
      } else {
        const virtualConnection: IPublishedConnectionWithTablesItem = row.data.virtualConnection;
        const tablesMap = _.indexBy(virtualConnection.tables, 'luid');
		// TFSID 1446844 looking for name of an id not found causes the whole task page to blank, we could throw an error if there is null
        const tableNames = row.data.extractServiceTask.startingItemIds.map((id: string) => tablesMap[id]?.name || null).sort();
        return {
          text: this.generatePublishedConnectionTaskName(virtualConnection, tableNames),
          title: this.generatePublishedConnectionTaskTitle(virtualConnection, tableNames),
          column: CommonColumn.ExtractTaskName,
          url: urls.virtualConnectionUrl(virtualConnection.id),
          testId: testId ? testId : 'name-' + row.key,
        };
      }
    } catch (e) {
      return {
        text: Messages.errorGeneratingLinkCell(),
        url: '#',
      };
    }
  };

  private generateLinkForExtractScheduleType = (row: DataGrid.Row, testId?: string): ILink => {
    try {
      const urls = this.params.urls.forSiteName(row.data.site.urlName);
        const virtualConnection: IPublishedConnectionWithTablesItem = row.data.virtualConnection;
        const tablesMap = _.indexBy(virtualConnection.tables, 'luid');
        const tableNames = row.data.extractServiceTask.startingItemIds.map((id: string) => tablesMap[id]?.name || null).sort();
        return {
          text: this.generatePublishedConnectionTaskName(virtualConnection, tableNames),
          title: this.generatePublishedConnectionTaskTitle(virtualConnection, tableNames),
          column: CommonColumn.ExtractTaskName,
          url: urls.virtualConnectionUrl(virtualConnection.id),
          testId: testId ? testId : 'name-' + row.key,
        };
    } catch (e) {
      return {
        text: Messages.errorGeneratingLinkCell(),
        url: '#',
      };
    }
  };

  private generatePublishedConnectionTaskName = (publishedConnection: IPublishedConnectionWithTablesItem, tables: ReadonlyArray<string>) => {
    const tableCount = tables.length;
    if (tableCount === 1) {
      return FeatureFlags.vconnRunHistory ? Messages.publishedConnectionExtractTableCount({TABLE_COUNT: tableCount, COUNT_LOC: Formatters.formatCount(tableCount)}) : Messages.messageWithColonSeparator({
        MESSAGE_PART_1: publishedConnection.name,
        MESSAGE_PART_2: tables[0]
      });
    } else if (tableCount > 1) {
      return FeatureFlags.vconnRunHistory ? Messages.publishedConnectionExtractTableCount({TABLE_COUNT: tableCount, COUNT_LOC: Formatters.formatCount(tableCount)}) : Messages.publishedConnectionScheduledTaskNameLink({
        PUBLISHED_CONNECTION_NAME: FeatureFlags.vconnRunHistory ? Messages.extract() : publishedConnection.name,
        TABLE_NAME: tables[0],
        TASK_COUNT_LOC: Formatters.formatCount(tableCount - 1)
      });
    }
    return publishedConnection.name;
  };

  private generatePublishedConnectionTaskTitle = (publishedConnection: IPublishedConnectionWithTablesItem, tables: ReadonlyArray<string>) => {
    if (tables.length > 0) {
      const tableNames = tables.join(Messages.commaWithSpace());
      return Messages.messageWithColonSeparator({
        MESSAGE_PART_1: publishedConnection.name,
        MESSAGE_PART_2: tableNames
      });
    }
    return publishedConnection.name;
  };

  private generateLinkForAccelerationTaskName = (row: DataGrid.Row, testId?: string): ILink => {
    const view = row.data.view as IView;

    return {
      text: view.name,
      column: CommonColumn.AccelerationTaskName,
      url: this.getContentUrl({
        key: view.id,
        data: view,
      }),
      testId: testId ? testId : 'name-' + row.key,
    };
  };

  private generateLinkForWorkbookDatasourceName = (row: DataGrid.Row, testId?: string): ILink => {
    const name = row.data && row.data.workbook ? row.data.workbook.name : row.data.datasource.name;
    const urls = this.params.urls.forSiteName(row.data.site.urlName);
    if (row.data.datasource) {
      const datasource: IDataSourceInfo = row.data.datasource;
      return {
        text: datasource.name,
        column: CommonColumn.WorkbookDatasourceName,
        url: urls.datasourceUrl(datasource.id),
        iconClassName: 'tb-grid-icon-datasource',
        iconTitle: Messages.datasource(),
      };
    } else {
      const workbook: IWorkbookInfo = row.data.workbook;
      return {
        text: workbook.name,
        column: CommonColumn.WorkbookDatasourceName,
        url: urls.workbookUrl(workbook.id),
        iconClassName: 'tb-grid-icon-workbook',
        iconTitle: Messages.workbook(),
      };
    }
  };

  private generateTitleForName = (row: DataGrid.Row, title: string): string => {
    if (this.isEmbeddedDatasource(row)) {
      return Messages.datasourceEmbeddedInWorkbook_nameTooltip({
        DATASOURCE_NAME: row.data.name,
        WORKBOOK_NAME: row.data.workbook.name
      });
    }

    return L10n.elideIfLong(title, 511); // IE only allows up to 512 characters in tooltips (unlike Firefox/Chrome)
  };

  private generateLinkForOriginalViewName = (row: DataGrid.Row, testId?: string): ILink => {
    const viewName = row.data.viewName;

    const url = this.params.urls.redirectToViewUrl(row.data.viewId);

    return {
      text: viewName,
      column: CommonColumn.OriginalViewName,
      url: url,
      displayJustText: isWorkbookSpace(this.params.spaceInfo) && this.isEmbeddedDatasource(row),
      testId: testId ? testId : 'name-' + row.key,
      isAccessDenied: this.isAccessDenied(row),
    };
  };

  private generateLinkForOwner = (row: DataGrid.Row): ILink => {
    // detail view info's stores owner as part of workbook object
    const useWorkbookOwner = row.data.contentType === 'view' && (!this.params.showingMixedContent || isRecentsSpace(this.params.spaceInfo));
    // database and table doesn't have owner field
    const showEmptyOwner = this.isDatabaseContentType(row) || this.isTableContentType(row);

    let owner = null;
    if (showEmptyOwner) {
      owner = null;
    } else if (useWorkbookOwner && row.data.workbook) {
      owner = row.data.workbook.owner;
    } else {
      owner = row.data.owner;
    }

    if (!owner) {
      return { text: '', url: undefined };
    }

    const displayName = owner.displayName;
    const url = this.params.urls.userUrl(owner.username, owner.domainName);

    return {
      text: displayName,
      url: url,
      style: owner.isHiddenInternalUser ? HiddenUserStyles.HiddenUserStyle : null,
      displayJustText: owner.isHiddenInternalUser,
      testId: 'list-owner-cell-' + row.data.contentType + '-' + row.data.id,
      isAccessDenied: this.isAccessDenied(row),
    };
  };

  private generateLinkForOwnerWithIcon = (row: DataGrid.Row): ILink => {
    const linkCell: ILink = this.generateLinkForOwner(row);

    linkCell.iconClassName = 'tb-grid-icon-user';
    linkCell.iconTitle = Messages.user();

    return linkCell;
  };

  private generateLinkForMetricConnectedView = (row: DataGrid.Row): ILink => {
    if (row.data && row.data.connectedView) {
      const connectedView: IConnectedViewInfo = row.data.connectedView;
      const viewName = connectedView.isViewModified ? connectedView.name + '*' : connectedView.name;

      const url = this.params.urls.viewUrl(connectedView.path);

      return {
        text: viewName,
        url: url
      };
    } else {
      return {
        text: Messages.unavailable(),
        url: '',
        style: GreyedOutTextStyle,
        displayJustText: true
      };
    }
  };

  private generateLinkForSubscribedBy = (row: DataGrid.Row): ILink => {
    const subscription = row.data as ISubscriptionInfo;
    const subscribedBy = subscription.creator;
    if (!subscribedBy) {
      return { text: '', url: undefined };
    }
    const displayName = subscribedBy.displayName;
    const url = new SpacesSiteUrls({ siteUrlName: subscription.site.urlName }).userUrl(subscribedBy.username, subscribedBy.domainName);
    return {
      text: displayName,
      url: url,
      style: subscribedBy.isHiddenInternalUser ? HiddenUserStyles.HiddenUserStyle as React.CSSProperties : null,
      displayJustText: subscribedBy.isHiddenInternalUser
    };
  };

  private generateLinkForModifiedBy = (row: DataGrid.Row): ILink => {
    const useWorkbookModifiedBy = (!this.params.showingMixedContent && row.data.contentType === 'workbook');
    const modifiedBy = useWorkbookModifiedBy && row.data.workbook ?
      row.data.workbook.modifiedBy : row.data.modifiedBy;

    if (!modifiedBy) {
      return { text: '', url: undefined };
    }

    const displayName = modifiedBy.displayName;
    const url = this.params.urls.userUrl(modifiedBy.username, modifiedBy.domainName);

    return {
      text: displayName,
      url: url,
      style: modifiedBy.isHiddenInternalUser ? HiddenUserStyles.HiddenUserStyle as React.CSSProperties : null,
      displayJustText: modifiedBy.isHiddenInternalUser
    };
  };


  private generateLinkForWorkbook = (row: DataGrid.Row): ILink => {
    return row.data.workbook && {
      text: row.data.workbook.name,
      url: this.params.urls.workbookUrl(row.data.workbook.id)
    };
  };

  private generateAlert = (row: DataGrid.Row): IAlert => {
    return {
      type: row.data.contentType,
    };
  };

  private generateLinkForSiteName = (row: DataGrid.Row): ILink => {
    const site = row.data as ISiteSettingsForServerAdmin;
    return {
      text: site.settings.name,
      url: this.params.urls.forSiteName(site.settings.urlName).projectsUrl(),
      iconClassName: 'tb-grid-icon-site-stylish',
      iconTitle: Messages.site()
    };
  };

  private generateLinkForScheduleName = (row: DataGrid.Row): ILink => {
    const schedule = row.data as IScheduleInfo;
    let displayName = schedule.name;
    if (this.params.displayScheduleDescriptionAsName) {
      displayName = displayName + ' - ' + IScheduleInfo.getScheduleDescription(schedule);
    }
    return {
      text: displayName,
      url: this.params.inServerArea
        ? serverScheduleUrl(ServerUrls.getInstance(), schedule.id, schedule.scheduledAction)
        : siteScheduleUrl(this.params.urls, schedule.id, schedule.scheduledAction),
      iconClassName: 'tb-grid-icon-schedule-stylish',
      iconTitle: Messages.schedule()
    };
  };

  private generateLinkForGroupName = (row: DataGrid.Row): ILink => {
    const group = row.data as IGroupInfo;
    let displayName = group.name;
    displayName = displayName.replace(/\n/g, ' ');
    return {
      text: displayName,
      url: this.params.urls.groupUrl(group.id),
      // TFSID: 1186001 - added to prevent browser truncation of whitespace in the display text.
      style: { whiteSpace: 'pre' },
      iconClassName: 'tb-grid-icon-group-stylish',
      iconTitle: Messages.group()
    };
  };

  private generateLinkForGroupSetName = (row: DataGrid.Row): ILink => {
    const group = row.data as IGroupSetInfo;
    let displayName = group.name;
    displayName = displayName.replace(/\n/g, ' ');
    return {
      text: displayName,
      url: this.params.urls.groupSetGroupsUrl(group.id),
      // TFSID: 1186001 - added to prevent browser truncation of whitespace in the display text.
      style: { whiteSpace: 'pre' },
      iconClassName: 'tb-grid-icon-groupset-stylish',
      iconTitle: Messages.groupSet()
    };
  };

  private generateDSCertificationTooltipProps = (row: DataGrid.Row): CertificationWatermark.CertificationTooltipProps => {
    const contentDetail = row.data.contentDetail || row.data;
    const certifier = contentDetail.certificationDetails ? UserMapper.mapOne(contentDetail.certificationDetails.certifier) : null;
    return contentDetail.certificationDetails &&
    {
      id: row.data.id,
      certifier: certifier && {
        customUserStyle: certifier.isHiddenInternalUser ? HiddenUserStyles.HiddenUserStyle as React.CSSProperties : null,
        displayName: certifier.displayName,
        url: certifier.domainName
          ? this.params.urls.userUrl(certifier.username, certifier.domainName)
          : ''
      },
      note: contentDetail.certificationDetails.certificationNote,
      date: MapperHelper.toDate(contentDetail.lastRefreshedAt)
    };
  };

  private generateActionsOptions = (): ActionCellOptions => {
    return {
      getContentItem: (row: DataGrid.Row) => row.data as IContentInfo,
      isRowSelected: this.isRowSelected,
      isAccessDenied: this.isAccessDenied,
      isRowDisabled: this.isRowDisabled
    };
  };

  private showingOnlyDataConnections = (): boolean => {
    return (this.params.contentFilter.length === 1
      && this.params.contentFilter[0] === 'dataconnection') || isDatasourceConnectionSubSpace(this.params.spaceInfo);
  };

  private siteUserDataGridColumn = (sortableHeaderFormatter: any): DataGrid.Column => {
    return {
      name: CommonColumn.SiteUserDisplayName,
      dataKey: OrderKeys.displayName,
      sortable: true,
      direction: 'asc',
      growToFit: true,
      headerText: Messages.column_fullName(),
      headerFormatter: sortableHeaderFormatter,
      cellFormatter: UserDataGridCells.ProfilePictureCell(
        (row: DataGrid.Row) => {
          const user = row.data as ISiteUserInfo;
          return {
            profileDisplayName: user.displayName,
            imageUrl: user.userImageUrl,
            divShape: ProfilePicture.Shape.Circle
          };
        },
        (row: DataGrid.Row) => {
          const user = row.data as ISiteUserInfo;
          const disabled = user.siteRole === SiteRoles.Guest;
          return {
            text: user.displayName,
            url: disabled ? null : this.params.urls.userUrl(user.username, user.domainName),
            style: disabled ? { color: CommonStyles.Colors.Grey102, textDecoration: 'none', pointerEvents: 'none' } : null
          };
        }
      )
    };
  };

  private publishedConnectionExtractStatus = (lastRefreshedAt?: Date): string => {
    return lastRefreshedAt ? Messages.virtualConnectionExtractWithTime({EXTRACT_REFRESH_LOC:L10n.localizedDateTime(lastRefreshedAt)}): Messages.live();
  };

  private publishedConnectionTableSync = (extractServiceTask?: IExtractServiceTask): string => {
    if (extractServiceTask && getPublishedConnectionExtractRefreshLevel(extractServiceTask.transactionLevel) === PublishedConnectionExtractRefreshLevel.Database){
      return Messages.publishedConnectionTableSyncRefresh();
    } else {
      return Messages.publishedConnectionTableIndependentRefresh();
    }
  };

  private invalidForModified = (row: DataGrid.Row): boolean => {
    return this.isTableContentType(row) || this.isDatabaseContentType(row);
  };
}


